// Properties — правая панель (контекстная).
//
// Показывает форму активной операции (extrude, fillet и т.д.) с её
// параметрами. После «OK» операция применяется, тело создаётся.

import React from 'react';
import { toast } from './Toasts';
import RU from '../i18n/ru';
import useCadStore from '../store/useCadStore';
import ExprInput from './ExprInput';
import JointForm from './JointForm';
import RibForm from './RibForm';
import { getGeometryClient } from '../geometryClient';
import { findProfiles, profilePolygonVertices, buildProfilePayloadWithHoles } from '../sketch/sketchProfiles';
import { rebuildAllBodies } from '../rebuildPipeline';
import { getBodyIndex, getEdgeEndpointsForBody, getEdgeDescriptorsForBody } from '../bodyHistoryHelper';
import cl from './Properties.module.css';


// Модуль-level защита от повторного запуска операции (двойной клик по OK).
// Одновременно активна одна форма, поэтому единого флага достаточно как
// подстраховки даже там, где нет локального busy-состояния.
let _opInFlight = false;
async function runExclusive(fn) {
    if (_opInFlight) return;
    _opInFlight = true;
    try { await fn(); }
    finally { _opInFlight = false; }
}


// Хук защиты от двойного клика по OK при долгой операции ядра.
// Возвращает [busy, run] — run(asyncFn) не запускает fn повторно, пока
// предыдущая не завершилась; busy используем для дизейбла кнопки + спиннера.
function useBusyGuard() {
    const [busy, setBusy] = React.useState(false);
    const inflight = React.useRef(false);
    const run = React.useCallback(async (fn) => {
        if (inflight.current) return;
        inflight.current = true;
        setBusy(true);
        try { await fn(); }
        finally { inflight.current = false; setBusy(false); }
    }, []);
    return [busy, run];
}


const ExtrudeForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);
    const setSelection = useCadStore((s) => s.setSelection);

    if (!pendingFeature || pendingFeature.kind !== 'extrude') return null;

    const { profileRefs, faceProfile, faceRef, distance, direction, operation, editingFeatureId } = pendingFeature;
    const hasProfile = profileRefs && profileRefs.length > 0;
    /*
     * Профиль эскиза ПЕРЕВЕШИВАЕТ грань. Ветка face-extrude берёт грань
     * целиком, поэтому при живых profileRefs она бы выдавила весь круг вместо
     * выбранного фрагмента. Условие держим здесь, у самой развилки, чтобы
     * любой путь, дописавший faceProfile попутно, не менял смысл операции.
     */
    const hasFace    = !!faceProfile && !hasProfile;
    const hasInput   = hasProfile || hasFace;
    const isEditing = !!editingFeatureId;

    // Профиль лежит на грани существующего тела? (face-extrude, либо sketch,
    // созданный на грани — planeRef.kind==='face'). Для таких — авто-операция
    // вырезать/добавить по направлению, как в Fusion.
    const sketchOfProfile = hasProfile
        ? sketches.find((s) => s.id === (profileRefs[0] || '').split('/')[0])
        : null;
    const profileOnBody = hasFace || sketchOfProfile?.planeRef?.kind === 'face';
    const faceSign = hasFace ? (faceProfile.sign || 1) : 1;

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasInput) {
            toast('Выбери профиль эскиза или грань тела', 'error');
            return;
        }

        // === Ветка 1: extrude грани тела (Press Pull) ===
        if (hasFace) {
            // sign из faceProfile компенсирует zDir плоскости replicad
            // (XZ/YZ имеют zDir не совпадающий с world axis). Направление
            // выдавливания (наружу/внутрь) задаёт ЮЗЕР через знак distance —
            // автоматическая инверсия по operation НЕ применяется, иначе
            // preview перепрыгивает при смене operation в форме.
            const baseSign = (faceProfile.sign || 1);
            const effectiveDistance = distance * baseSign;
            const client = getGeometryClient();
            try {
                await client.extrudeSketchProfile({
                    /* holes — внутренние контуры кольцевой грани.
                       Без них вырезание по такой грани падает в OCCT. */
                    profile: {
                        kind: 'polygon',
                        verts: faceProfile.verts,
                        ...(faceProfile.holes ? { holes: faceProfile.holes } : {}),
                    },
                    planeName: faceProfile.planeName,
                    planeOffset: faceProfile.planeOffset,
                    customPlane: faceProfile.customPlane || null,
                    distance: effectiveDistance,
                    direction,
                    operation,
                });
                addFeature({
                    id: `feat_${Date.now().toString(36)}`,
                    kind: 'extrude',
                    faceRef,
                    faceProfile,
                    distance,
                    direction,
                    operation,
                    // Индекс body на которое применяется face-extrude (для rebuild)
                    targetBodyIdx: getBodyIndex(pendingFeature.targetBodyId),
                    createdAt: Date.now(),
                });
            } catch (e) {
                console.error('[ExtrudeForm] face extrude failed:', e);
                toast('Ошибка выдавливания грани: ' + e.message, 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        // === Ветка 2: extrude профиля(ей) эскиза (классика) ===
        const ref0 = profileRefs[0];
        const [sketchId0] = ref0.split('/');
        const sketch0 = sketches.find((s) => s.id === sketchId0);
        if (!sketch0) {
            toast('Эскиз не найден', 'error');
            return;
        }

        if (isEditing) {
            // === Редактирование существующей фичи ===
            updateFeature(editingFeatureId, {
                profileRefs: [...profileRefs],
                distance,
                direction,
                operation,
            });
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[ExtrudeForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + e.message, 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        // === Создание новой фичи для КАЖДОГО выбранного профиля ===
        // При множественном выборе (Ctrl) — применяем операцию (напр. вырезать)
        // ко всем профилям одной командой: цикл по profileRefs.
        const client = getGeometryClient();
        let anyDone = false;
        for (const ref of profileRefs) {
            const [sid, profileId] = ref.split('/');
            const sk = sketches.find((s) => s.id === sid);
            if (!sk) continue;
            const profs = findProfiles(sk);
            const profile = profs.find((p) => p.id === profileId);
            if (!profile) continue;
            const profilePayload = buildProfilePayloadWithHoles(profile, profs, sk);
            if (!profilePayload) continue;

            const planeName = sk.planeRef?.plane || 'XY';
            const planeOffset = sk.planeRef?.offset || 0;
            let customPlane = null;
            if (planeName === 'CUSTOM') {
                if (sk.planeRef?.origin && sk.planeRef?.xDir && sk.planeRef?.normal) {
                    customPlane = {
                        origin: sk.planeRef.origin, xDir: sk.planeRef.xDir, normal: sk.planeRef.normal,
                    };
                } else {
                    toast('Эскиз на наклонной плоскости без данных базиса — пересоздай эскиз.', 'error');
                    continue;
                }
            }
            // Целевое тело для add/cut — тело, на грани которого лежит эскиз.
            // Без него worker брал «последнее тело», из-за чего при add
            // основной цилиндр мог не попасть в объединение / потеряться.
            const targetBodyId = sk.planeRef?.bodyId || null;
            try {
                await client.extrudeSketchProfile({
                    profile: profilePayload,
                    planeName, planeOffset, customPlane,
                    distance, direction, operation,
                    targetBodyId,
                });
                addFeature({
                    id: `feat_${Date.now().toString(36)}_${profileId}`,
                    kind: 'extrude',
                    profileRefs: [ref],
                    distance, direction, operation,
                    targetBodyIdx: getBodyIndex(targetBodyId),
                    createdAt: Date.now(),
                });
                anyDone = true;
            } catch (e) {
                console.error('[ExtrudeForm] extrude failed for', ref, e);
                toast('Ошибка выдавливания: ' + e.message, 'error');
            }
        }
        if (!anyDone) return;

        setPendingFeature(null);
        setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>
                {isEditing ? 'Изменить выдавливание' : RU.create.extrude}
            </div>

            <FormRow label={hasFace ? 'Грань' : RU.operationParams.profile}>
                {hasFace ? (
                    <span className={cl.profileBadge}>✓ Грань тела выбрана</span>
                ) : hasProfile ? (
                    <span className={cl.profileBadge}>✓ Профиль выбран</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>Кликни по контуру эскиза или грани тела</span>
                )}
            </FormRow>

            {/* Расстояние принимает ФОРМУЛУ, а не только число: «толщина * 3»,
                «sqrt(16) + 2», ссылки на параметры — как в Fusion. Обычный
                input[type=number] такого не позволял в принципе. */}
            <FormRow label={RU.operationParams.distance}>
                <ExprInput
                    value={pendingFeature.distanceExpr ?? distance}
                    unit="мм"
                    /*
                     * Фокус сразу в расстоянии — но ТОЛЬКО когда профиль уже
                     * выбран (hasInput). Пока человек ещё тычет в грань или
                     * контур, поле забирать фокус не должно: следующий
                     * осмысленный шаг — выбор, а не ввод числа.
                     *
                     * Когда профиль есть, ввести расстояние — единственное,
                     * что остаётся сделать, и лишний клик по полю здесь
                     * ничего не решает.
                     */
                    autoFocus={hasInput}
                    onChange={(dist, expr) => {
                        const patch = { distance: dist, distanceExpr: expr };
                        // Авто-операция по направлению, когда профиль на грани
                        // тела (face-extrude ИЛИ эскиз на грани): внутрь=вырез,
                        // наружу=добавить. effectiveDist = dist * faceSign.
                        const eff = dist * faceSign;
                        if (profileOnBody && !pendingFeature.operationManual && eff !== 0) {
                            patch.operation = eff < 0 ? 'cut' : 'add';
                        }
                        updatePendingFeature(patch);
                    }}
                />
            </FormRow>

            <FormRow label={RU.operationParams.direction}>
                <select
                    className={cl.select}
                    value={direction}
                    onChange={(e) => updatePendingFeature({ direction: e.target.value })}
                >
                    <option value="one">{RU.operationParams.directionOne}</option>
                    <option value="symmetric">{RU.operationParams.directionSymmetric}</option>
                    <option value="two">{RU.operationParams.directionTwo}</option>
                </select>
            </FormRow>

            <FormRow label={RU.operationParams.operation}>
                <select
                    className={cl.select}
                    value={operation}
                    onChange={(e) => updatePendingFeature({
                        operation: e.target.value,
                        // Ручной выбор — фиксируем, чтобы авто-переключение по
                        // направлению (внутрь=вырез) больше не перебивало юзера.
                        operationManual: true,
                    })}
                >
                    <option value="new">{RU.operationParams.opNew}</option>
                    <option value="add">{RU.operationParams.opJoin}</option>
                    <option value="cut">{RU.operationParams.opCut}</option>
                    <option value="intersect">{RU.operationParams.opIntersect}</option>
                </select>
            </FormRow>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasInput}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


const FormRow = ({ label, children }) => (
    <div className={cl.formRow}>
        <div className={cl.formLabel}>{label}</div>
        <div className={cl.formField}>{children}</div>
    </div>
);


// =====================================================================
// === PrimitiveForm — Box / Cylinder / Sphere / Torus ===============
// =====================================================================
//
// Создаёт готовый примитив без эскиза. Параметры зависят от вида:
//   box      — width, depth, height
//   cylinder — radius, height
//   sphere   — radius
//   torus    — radius (кольца), tube (трубы)
//
// Тело создаётся через worker.createPrimitive(payload) и сохраняется как
// feature {kind:'primitive', primitiveKind, ...} в timeline.
// Edit-mode: ✎ в timeline загружает параметры, OK → updateFeature + rebuild.

const PRIMITIVE_LABELS = {
    box:      'Параллелепипед',
    cylinder: 'Цилиндр',
    sphere:   'Сфера',
    torus:    'Тор',
};
const PRIMITIVE_ICONS = {
    box: '', cylinder: '', sphere: '', torus: '',
};

const PrimitiveForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);
    const setSelection = useCadStore((s) => s.setSelection);

    // Live-update: при первом монтировании сразу создаём тело-preview,
    // дальше каждое изменение параметра / position пересоздаёт его в worker'е
    // (debounce 120мс чтобы не гонять worker при каждом наборе клавиш).
    // bodyId хранится в pendingFeature.bodyId (как ID превью-тела в worker).
    const debounceRef = React.useRef(null);

    // Ключ который описывает «всё что влияет на форму тела» — при его смене
    // дёргаем worker. Сравнение через JSON чтобы не ловить ложные ререндеры.
    const f = pendingFeature;
    const isPrim = f && f.kind === 'primitive';
    const shapeKey = isPrim ? JSON.stringify({
        k: f.primitiveKind,
        w: f.width, d: f.depth, h: f.height,
        r: f.radius, t: f.tube,
        p: f.position || [0, 0, 0],
    }) : null;

    // Build payload по текущему pendingFeature
    const buildPayload = React.useCallback((override = {}) => {
        const cur = useCadStore.getState().pendingFeature;
        if (!cur) return null;
        const base = { primitiveKind: cur.primitiveKind, position: cur.position || [0, 0, 0] };
        let body;
        if (cur.primitiveKind === 'box') {
            body = { width: cur.width, depth: cur.depth, height: cur.height };
        } else if (cur.primitiveKind === 'cylinder') {
            body = { radius: cur.radius, height: cur.height };
        } else if (cur.primitiveKind === 'sphere') {
            body = { radius: cur.radius };
        } else {
            body = { radius: cur.radius, tube: cur.tube };
        }
        return { ...base, ...body, ...override };
    }, []);

    const isValid = React.useCallback(() => {
        const cur = useCadStore.getState().pendingFeature;
        if (!cur) return false;
        const k = cur.primitiveKind;
        if (k === 'box')      return cur.width > 0 && cur.depth > 0 && cur.height > 0;
        if (k === 'cylinder') return cur.radius > 0 && cur.height > 0;
        if (k === 'sphere')   return cur.radius > 0;
        if (k === 'torus')    return cur.radius > 0 && cur.tube > 0 && cur.tube < cur.radius;
        return false;
    }, []);

    // Live-create / live-update тела при изменении shapeKey.
    // Только в create-mode (без editingFeatureId) — для edit-mode у нас уже
    // есть тело в сцене и rebuildAllBodies при OK сделает корректную пересборку.
    const isEditingFlag = !!pendingFeature?.editingFeatureId;
    const liveInflightRef = React.useRef(false);

    // Внутренний executor live-update'а. Обновляет тело в worker'е
    // не чаще чем «один запрос в полёте» (если уже есть — пропускаем,
    // финальное состояние pendingFeature всё равно догонит).
    const runLiveUpdate = React.useCallback(async (override = {}) => {
        if (!isPrim || isEditingFlag || !isValid()) return;
        if (liveInflightRef.current) return;
        liveInflightRef.current = true;
        try {
            const cur = useCadStore.getState().pendingFeature;
            if (!cur || cur.kind !== 'primitive') return;
            const payload = buildPayload({
                ...override,
                replaceBodyId: cur.bodyId || null,
            });
            const res = await getGeometryClient().createPrimitive(payload);
            if (res?.bodyId && res.bodyId !== cur.bodyId) {
                useCadStore.getState().updatePendingFeature({ bodyId: res.bodyId });
                useCadStore.getState().setSelection('body', [res.bodyId]);
            }
        } catch (e) {
            console.warn('[PrimitiveForm] live-update failed:', e?.message || e);
        } finally {
            liveInflightRef.current = false;
        }
    }, [isPrim, isEditingFlag, isValid, buildPayload]);

    // Глобальный hook для TransformGizmo — он дёргает live-update
    // на каждый pointermove drag'а (без debounce).
    React.useEffect(() => {
        window.__kubikonCadPrimitiveLive = (override) => {
            // Если ещё есть запрос — пропускаем; следующий тик догонит.
            runLiveUpdate(override);
        };
        return () => { delete window.__kubikonCadPrimitiveLive; };
    }, [runLiveUpdate]);

    // Debounced update от инпутов размеров (на каждый набор клавиш — лишний воркер-вызов)
    React.useEffect(() => {
        if (!isPrim) return;
        if (isEditingFlag) return;
        if (!isValid()) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => runLiveUpdate(), 120);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shapeKey, isPrim, isEditingFlag]);

    if (!isPrim) return null;

    const { primitiveKind, editingFeatureId, bodyId } = pendingFeature;
    const isEditing = !!editingFeatureId;
    const label = PRIMITIVE_LABELS[primitiveKind] || primitiveKind;
    const position = pendingFeature.position || [0, 0, 0];

    const onCancel = () => {
        // Удаляем превью-тело из worker'а и store
        if (bodyId && !isEditing) {
            try { getGeometryClient().deleteBody(bodyId); } catch (_) {}
            useCadStore.getState().removeBody(bodyId);
        }
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!isValid()) {
            toast('Проверь параметры — все размеры должны быть > 0' +
                (primitiveKind === 'torus' ? ', tube < radius' : ''), 'error');
            return;
        }

        const payload = buildPayload();

        if (isEditing) {
            // Edit-mode: обновляем фичу и пересчитываем сцену
            updateFeature(editingFeatureId, payload);
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[PrimitiveForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + e.message, 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        // Create-mode: тело уже создано (live-preview), просто фиксируем фичу.
        addFeature({
            id: `feat_${Date.now().toString(36)}`,
            kind: 'primitive',
            ...payload,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
        setSelection('body', bodyId ? [bodyId] : []);
    });

    const numField = (key, hint) => (
        <FormRow label={hint}>
            <div className={cl.inputWithUnit}>
                <input
                    type="number"
                    step="any"
                    min="0.1"
                    className={cl.numInput}
                    value={pendingFeature[key] ?? ''}
                    onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        updatePendingFeature({ [key]: isNaN(v) ? 0 : v });
                    }}
                />
                <span className={cl.unit}>мм</span>
            </div>
        </FormRow>
    );

    const posField = (idx, hint) => (
        <FormRow label={hint}>
            <div className={cl.inputWithUnit}>
                <input
                    type="number"
                    step="any"
                    className={cl.numInput}
                    value={position[idx] ?? 0}
                    onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        const next = [...position];
                        next[idx] = isNaN(v) ? 0 : v;
                        updatePendingFeature({ position: next });
                    }}
                />
                <span className={cl.unit}>мм</span>
            </div>
        </FormRow>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>
                {PRIMITIVE_ICONS[primitiveKind]} {isEditing ? `Изменить — ${label}` : label}
            </div>

            {/* Размеры */}
            {primitiveKind === 'box' && (
                <>
                    {numField('width',  'Ширина (X)')}
                    {numField('depth',  'Глубина (Y)')}
                    {numField('height', 'Высота (Z)')}
                </>
            )}
            {primitiveKind === 'cylinder' && (
                <>
                    {numField('radius', 'Радиус')}
                    {numField('height', 'Высота')}
                </>
            )}
            {primitiveKind === 'sphere' && (
                <>
                    {numField('radius', 'Радиус')}
                </>
            )}
            {primitiveKind === 'torus' && (
                <>
                    {numField('radius', 'Радиус кольца')}
                    {numField('tube',   'Радиус трубы')}
                </>
            )}

            {/* Позиция тела (X / Y / Z) — синхронизирована с TransformGizmo в сцене */}
            <div className={cl.sectionTitle} style={{ marginTop: 14 }}>Позиция</div>
            {posField(0, 'X')}
            {posField(1, 'Y')}
            {posField(2, 'Z')}

            <div className={cl.formActions}>
                <button className={`${cl.btn} ${cl.btnPrimary}`} onClick={onOk}>
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


/*
 * === ScaleForm — масштаб тела ===
 *
 * Нужен при подготовке к печати: модель нарисована в одном размере, а печатать
 * надо крупнее или мельче. Перестраивать ради этого всю историю не хочется.
 * Масштабирование идёт от центра габаритов, поэтому деталь остаётся на месте.
 */
const ScaleForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const setSelection = useCadStore((s) => s.setSelection);
    const selection = useCadStore((s) => s.selection);
    const addFeature = useCadStore((s) => s.addFeature);
    const [busy, runGuarded] = useBusyGuard();

    const factor = pendingFeature?.factor ?? 2;
    const bodyId = pendingFeature?.bodyId
        || (selection.kind === 'body' ? selection.ids[0] : null);

    const onOk = () => runGuarded(async () => {
        if (!bodyId) { toast('Выбери тело', 'error'); return; }
        try {
            await getGeometryClient().scaleBody(bodyId, factor);
            addFeature({
                kind: 'scale', factor,
                targetBodyIdx: getBodyIndex(bodyId),
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[ScaleForm] scale failed:', e);
            toast('Не удалось масштабировать: ' + e.message, 'error');
            return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    const onCancel = () => { setPendingFeature(null); setSelection(null, []); };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Масштаб</div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Тело</span>
                <span className={bodyId ? cl.profileBadgeActive : cl.profileBadgeNeed}>
                    {bodyId ? 'выбрано' : 'кликни тело'}
                </span>
            </div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Во сколько раз</span>
                <span className={cl.inputWithUnit}>
                    <input className={cl.numInput} type="number" min="0.05" step="0.1" value={factor}
                        onChange={(e) => updatePendingFeature({ factor: Number(e.target.value) })}/>
                    <span className={cl.unit}>×</span>
                </span>
            </div>

            {/* Подсказка вместо устного счёта — сразу видно, что произойдёт. */}
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Результат</span>
                <span style={{ color: '#9aa4b2' }}>
                    {factor > 1 ? `больше в ${factor}` : factor < 1 && factor > 0
                        ? `меньше в ${(1 / factor).toFixed(2)}` : '—'} раз
                </span>
            </div>

            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={!bodyId || busy} onClick={onOk}>
                    {busy ? 'Считаю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={onCancel}>Отмена</button>
            </div>
        </div>
    );
};


/*
 * === CoilForm — спираль: пружина и резьба ===
 *
 * Нужна урокам методички: амортизатор (пружина) и крепёж с резьбой.
 * Параметры названы по-человечески: не «шаг винтовой линии», а «шаг витка»,
 * и рядом подсказка, сколько витков получится — детям так понятнее, чем
 * считать в уме height/pitch.
 */
const CoilForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const [busy, runGuarded] = useBusyGuard();

    const radius = pendingFeature?.radius ?? 20;
    const pitch = pendingFeature?.pitch ?? 8;
    const height = pendingFeature?.height ?? 40;
    const wireDiameter = pendingFeature?.wireDiameter ?? 3;
    const section = pendingFeature?.section ?? 'circle';
    const lefthand = pendingFeature?.lefthand ?? false;

    const turns = pitch > 0 ? (height / pitch) : 0;

    const onOk = () => runGuarded(async () => {
        try {
            const payload = { radius, pitch, height, wireDiameter, section, lefthand };
            await getGeometryClient().createCoil(payload);
            addFeature({ kind: 'coil', ...payload, createdAt: Date.now() });
        } catch (e) {
            console.error('[CoilForm] coil failed:', e);
            toast('Не удалось построить спираль: ' + e.message, 'error');
            return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    const onCancel = () => { setPendingFeature(null); setSelection(null, []); };

    const numRow = (label, value, field, unit = 'мм', min = 0.1, step = 1) => (
        <div className={cl.formRow}>
            <span className={cl.formLabel}>{label}</span>
            <span className={cl.inputWithUnit}>
                <input className={cl.numInput} type="number" min={min} step={step} value={value}
                    onChange={(e) => updatePendingFeature({ [field]: Number(e.target.value) })}/>
                <span className={cl.unit}>{unit}</span>
            </span>
        </div>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Спираль</div>
            {numRow('Радиус спирали', radius, 'radius')}
            {numRow('Шаг витка', pitch, 'pitch')}
            {numRow('Высота', height, 'height')}
            {numRow('Толщина', wireDiameter, 'wireDiameter', 'мм', 0.1, 0.5)}

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Сечение</span>
                <select className={cl.select} value={section}
                    onChange={(e) => updatePendingFeature({ section: e.target.value })}>
                    <option value="circle">Круглое (проволока)</option>
                    <option value="square">Квадратное</option>
                </select>
            </div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Навивка</span>
                <select className={cl.select} value={lefthand ? 'left' : 'right'}
                    onChange={(e) => updatePendingFeature({ lefthand: e.target.value === 'left' })}>
                    <option value="right">Правая (обычная)</option>
                    <option value="left">Левая</option>
                </select>
            </div>

            {/* Подсказка вместо устного счёта: сразу видно, что получится. */}
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Витков</span>
                <span style={{ color: '#9aa4b2' }}>{turns > 0 ? turns.toFixed(1) : '—'}</span>
            </div>

            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={busy} onClick={onOk}>
                    {busy ? 'Строю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={onCancel}>Отмена</button>
            </div>
        </div>
    );
};


/*
 * === TextForm — объёмный текст на плоскости ===
 *
 * Первый урок 3D-моделирования — брелок с именем, поэтому текст нужен в
 * интерфейсе, а не только в ядре. Три режима: отдельная деталь, надпись поверх
 * тела (add) и гравировка внутрь (cut) — последняя чаще всего и нужна.
 *
 * Плоскость выбирается как у примитивов: базовая XY/XZ/YZ или грань тела,
 * если она выделена (тогда буквы лягут на неё).
 */
const TextForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const [busy, runGuarded] = useBusyGuard();

    const text = pendingFeature?.text ?? '';
    const fontSize = pendingFeature?.fontSize ?? 10;
    const thickness = pendingFeature?.thickness ?? 2;
    const planeName = pendingFeature?.planeName ?? 'XY';
    const operation = pendingFeature?.operation ?? 'new';

    // Грань выбрана — буквы лягут на неё (как в 123D: клик по грани, потом «T»).
    const faceData = useCadStore((s) => s.selectedFaceData);
    const onFace = selection.kind === 'face' && !!faceData;

    const onOk = () => runGuarded(async () => {
        if (!text.trim()) { toast('Введи текст', 'error'); return; }
        const client = getGeometryClient();
        try {
            const payload = {
                text: text.trim(),
                fontSize: Number(fontSize) || 10,
                thickness: Number(thickness) || 2,
                operation,
            };
            if (onFace && faceData.planeRef) {
                // Грань тела: работаем в её плоскости.
                const pr = faceData.planeRef;
                if (pr.plane === 'CUSTOM') {
                    payload.customPlane = { origin: pr.origin, xDir: pr.xDir, normal: pr.normal };
                } else {
                    payload.planeName = pr.plane;
                    payload.planeOffset = pr.offset || 0;
                }
                if (operation !== 'new') {
                    payload.targetBodyId = faceData.bodyId;
                    // Индекс тела нужен для перестроения истории: runtime-id
                    // после rebuild другой, а позиция в bodyHistory стабильна.
                    payload.targetBodyIdx = getBodyIndex(faceData.bodyId);
                }
            } else {
                payload.planeName = planeName;
            }

            await client.createText(payload);
            addFeature({
                kind: 'text',
                text: payload.text,
                fontSize: payload.fontSize,
                thickness: payload.thickness,
                planeName: payload.planeName,
                planeOffset: payload.planeOffset,
                customPlane: payload.customPlane,
                targetBodyIdx: payload.targetBodyIdx,
                operation,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[TextForm] text failed:', e);
            toast('Не удалось создать текст: ' + e.message, 'error');
            return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    const onCancel = () => { setPendingFeature(null); setSelection(null, []); };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Текст</div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Надпись</span>
                <input
                    className={cl.numInput}
                    style={{ width: 150 }}
                    type="text"
                    value={text}
                    autoFocus
                    placeholder="Например: Максим"
                    onChange={(e) => updatePendingFeature({ text: e.target.value })}
                />
            </div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Размер букв</span>
                <span className={cl.inputWithUnit}>
                    <input className={cl.numInput} type="number" min="1" value={fontSize}
                        onChange={(e) => updatePendingFeature({ fontSize: Number(e.target.value) })}/>
                    <span className={cl.unit}>мм</span>
                </span>
            </div>

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Высота</span>
                <span className={cl.inputWithUnit}>
                    <input className={cl.numInput} type="number" min="0.2" step="0.2" value={thickness}
                        onChange={(e) => updatePendingFeature({ thickness: Number(e.target.value) })}/>
                    <span className={cl.unit}>мм</span>
                </span>
            </div>

            {/* Плоскость выбираем только когда грань НЕ выделена: иначе буквы
                кладём на неё, и выбор был бы обманом. */}
            {!onFace && (
                <div className={cl.formRow}>
                    <span className={cl.formLabel}>Плоскость</span>
                    <select className={cl.select} value={planeName}
                        onChange={(e) => updatePendingFeature({ planeName: e.target.value })}>
                        <option value="XY">Сверху (XY)</option>
                        <option value="XZ">Спереди (XZ)</option>
                        <option value="YZ">Сбоку (YZ)</option>
                    </select>
                </div>
            )}
            {onFace && (
                <div className={cl.formRow}>
                    <span className={cl.formLabel}>Плоскость</span>
                    <span className={cl.profileBadgeActive}>на выбранной грани</span>
                </div>
            )}

            <div className={cl.formRow}>
                <span className={cl.formLabel}>Операция</span>
                <select className={cl.select} value={operation}
                    onChange={(e) => updatePendingFeature({ operation: e.target.value })}>
                    <option value="new">Новое тело</option>
                    <option value="add">Надпись поверх</option>
                    <option value="cut">Гравировка (вырезать)</option>
                </select>
            </div>

            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={!text.trim() || busy} onClick={onOk}>
                    {busy ? 'Строю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={onCancel}>Отмена</button>
            </div>
        </div>
    );
};


// === RevolveForm — вращение профиля эскиза вокруг оси ===
const RevolveForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);
    const [busy, runGuarded] = useBusyGuard();

    // Пока форма открыта — клик по профилю кладётся в profileRefs.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'revolve') return;
        if (selection.kind === 'profile' && selection.ids.length > 0) {
            const cur = pendingFeature.profileRefs || [];
            if (cur.join() !== selection.ids.join()) {
                updatePendingFeature({ profileRefs: [...selection.ids] });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'revolve') return null;

    const { profileRefs, axis = 'sketch_Y', angle = 360, operation = 'new', editingFeatureId } = pendingFeature;
    const hasProfile = profileRefs && profileRefs.length > 0;
    const isEditing = !!editingFeatureId;

    const onCancel = () => { setPendingFeature(null); setSelection(null, []); };

    const onOk = () => runGuarded(async () => {
        if (!hasProfile) { toast('Выбери профиль эскиза для вращения', 'error'); return; }
        const ang = Number(angle);
        if (!(ang > 0 && ang <= 360)) { toast('Угол должен быть от 1 до 360°', 'error'); return; }

        // Edit-mode: меняем параметры фичи и пересобираем.
        if (isEditing) {
            updateFeature(editingFeatureId, {
                profileRefs: [...profileRefs], axis, angle: ang, operation,
            });
            try { await rebuildAllBodies(); }
            catch (e) { toast('Ошибка пересчёта: ' + e.message, 'error'); return; }
            setPendingFeature(null); setSelection(null, []);
            return;
        }

        // Создание: строим профиль (с дырками) и вращаем.
        const [sketchId, profileId] = profileRefs[0].split('/');
        const sketch = sketches.find((s) => s.id === sketchId);
        if (!sketch) { toast('Эскиз не найден', 'error'); return; }
        const profiles = findProfiles(sketch);
        const profile = profiles.find((p) => p.id === profileId);
        if (!profile) { toast('Профиль не найден', 'error'); return; }
        const profilePayload = buildProfilePayloadWithHoles(profile, profiles, sketch);
        if (!profilePayload) { toast('Контур не замкнут', 'error'); return; }

        const planeName = sketch.planeRef?.plane || 'XY';
        const planeOffset = sketch.planeRef?.offset || 0;
        let customPlane = null;
        if (planeName === 'CUSTOM' && sketch.planeRef?.origin) {
            customPlane = {
                origin: sketch.planeRef.origin,
                xDir: sketch.planeRef.xDir,
                normal: sketch.planeRef.normal,
            };
        }
        const axisVec = axis === 'sketch_X' ? [1, 0, 0] : [0, 1, 0];
        try {
            await getGeometryClient().revolveSketchProfile({
                profile: profilePayload, planeName, planeOffset, customPlane,
                axis: axisVec, angle: ang, operation,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'revolve',
                profileRefs: [...profileRefs],
                axis, angle: ang, operation,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[RevolveForm] revolve failed:', e);
            toast('Ошибка вращения: ' + e.message, 'error');
            return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{isEditing ? 'Изменить вращение' : 'Вращение'}</div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Профиль</span>
                <span className={hasProfile ? cl.profileBadgeActive : cl.profileBadgeNeed}>
                    {hasProfile ? 'выбран' : 'кликни профиль'}
                </span>
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Ось вращения</span>
                <select className={cl.select} value={axis}
                    onChange={(e) => updatePendingFeature({ axis: e.target.value })}>
                    <option value="sketch_Y">Вертикальная (Y эскиза)</option>
                    <option value="sketch_X">Горизонтальная (X эскиза)</option>
                </select>
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Угол</span>
                <span className={cl.inputWithUnit}>
                    <input className={cl.numInput} type="number" min="1" max="360" value={angle}
                        onChange={(e) => updatePendingFeature({ angle: Number(e.target.value) })}/>
                    <span className={cl.unit}>°</span>
                </span>
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Операция</span>
                <select className={cl.select} value={operation}
                    onChange={(e) => updatePendingFeature({ operation: e.target.value })}>
                    <option value="new">Новое тело</option>
                    <option value="add">Объединить</option>
                    <option value="cut">Вырезать</option>
                    <option value="intersect">Пересечь</option>
                </select>
            </div>
            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={!hasProfile || busy} onClick={onOk}>
                    {busy ? 'Строю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={onCancel}>Отмена</button>
            </div>
        </div>
    );
};


// Плоскость эскиза → формат для воркера (planeName/planeOffset/customPlane).
function sketchPlaneForWorker(sketch) {
    const pr = sketch?.planeRef || {};
    if (pr.plane === 'CUSTOM' && pr.origin && pr.xDir && pr.normal) {
        return { planeName: 'CUSTOM', customPlane: { origin: pr.origin, xDir: pr.xDir, normal: pr.normal } };
    }
    return { planeName: pr.plane || 'XY', planeOffset: pr.offset || 0 };
}

// Контур профиля (с дырками) по ref 'sketchId/profileId'.
function profileContourByRef(ref, sketches) {
    if (!ref) return null;
    const [sketchId, profileId] = ref.split('/');
    const sketch = sketches.find((s) => s.id === sketchId);
    if (!sketch) return null;
    const profiles = findProfiles(sketch);
    const profile = profiles.find((p) => p.id === profileId);
    if (!profile) return null;
    const payload = buildProfilePayloadWithHoles(profile, profiles, sketch);
    return payload ? { contour: payload, sketch } : null;
}


// === SweepForm — протянуть профиль вдоль траектории ===
const SweepForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const [busy, runGuarded] = useBusyGuard();

    // Клик по профилю: 1-й → сечение, 2-й → траектория.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'sweep') return;
        if (selection.kind === 'profile' && selection.ids.length > 0) {
            const ref = selection.ids[0];
            if (!pendingFeature.profileRef) updatePendingFeature({ profileRef: ref });
            else if (!pendingFeature.pathRef && ref !== pendingFeature.profileRef) {
                updatePendingFeature({ pathRef: ref });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'sweep') return null;
    const { profileRef, pathRef, operation = 'new' } = pendingFeature;
    const ready = !!profileRef && !!pathRef;

    const onOk = () => runGuarded(async () => {
        const prof = profileContourByRef(profileRef, sketches);
        const pathP = profileContourByRef(pathRef, sketches);
        if (!prof || !pathP) { toast('Выбери сечение и траекторию (2 профиля эскизов)', 'error'); return; }
        // Траектория — берём вершины полигона профиля-пути как открытую полилинию.
        const [pSketchId, pProfId] = pathRef.split('/');
        const pSketch = sketches.find((s) => s.id === pSketchId);
        const pProfiles = findProfiles(pSketch);
        const pProfile = pProfiles.find((p) => p.id === pProfId);
        const pathVerts = pProfile ? profilePolygonVertices(pProfile, pSketch) : null;
        if (!pathVerts || pathVerts.length < 2) { toast('Траектория должна быть линией/полилинией', 'error'); return; }
        try {
            await getGeometryClient().sweepProfile({
                profile: prof.contour,
                profilePlane: sketchPlaneForWorker(prof.sketch),
                path: { kind: 'polygon', verts: pathVerts },
                pathPlane: sketchPlaneForWorker(pathP.sketch),
                operation,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`, kind: 'sweep',
                profileRef, pathRef, operation, createdAt: Date.now(),
            });
        } catch (e) {
            toast('Ошибка sweep: ' + e.message, 'error'); return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Сдвиг по траектории (Sweep)</div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Сечение</span>
                <span className={profileRef ? cl.profileBadgeActive : cl.profileBadgeNeed}>
                    {profileRef ? 'выбрано' : 'кликни профиль'}
                </span>
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Траектория</span>
                <span className={pathRef ? cl.profileBadgeActive : cl.profileBadgeNeed}>
                    {pathRef ? 'выбрана' : 'кликни линию/дугу второго эскиза'}
                </span>
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Операция</span>
                <select className={cl.select} value={operation}
                    onChange={(e) => updatePendingFeature({ operation: e.target.value })}>
                    <option value="new">Новое тело</option>
                    <option value="add">Объединить</option>
                    <option value="cut">Вырезать</option>
                </select>
            </div>
            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={!ready || busy} onClick={onOk}>
                    {busy ? 'Строю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={() => { setPendingFeature(null); setSelection(null, []); }}>Отмена</button>
            </div>
        </div>
    );
};


// === LoftForm — переход между профилями на разных плоскостях ===
const LoftForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const sketches = useCadStore((s) => s.doc.sketches);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const [busy, runGuarded] = useBusyGuard();

    // Накапливаем профили по клику (в порядке выбора).
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'loft') return;
        if (selection.kind === 'profile' && selection.ids.length > 0) {
            const ref = selection.ids[0];
            const cur = pendingFeature.profileRefs || [];
            if (!cur.includes(ref)) updatePendingFeature({ profileRefs: [...cur, ref] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'loft') return null;
    const { profileRefs = [], operation = 'new' } = pendingFeature;
    const ready = profileRefs.length >= 2;

    const onOk = () => runGuarded(async () => {
        const profiles = [];
        for (const ref of profileRefs) {
            const p = profileContourByRef(ref, sketches);
            if (!p) { toast('Профиль не найден: ' + ref, 'error'); return; }
            profiles.push({ contour: p.contour, plane: sketchPlaneForWorker(p.sketch) });
        }
        try {
            await getGeometryClient().loftProfiles({ profiles, operation });
            addFeature({
                id: `feat_${Date.now().toString(36)}`, kind: 'loft',
                profileRefs: [...profileRefs], operation, createdAt: Date.now(),
            });
        } catch (e) {
            toast('Ошибка лофта: ' + e.message, 'error'); return;
        }
        setPendingFeature(null); setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Лофт (переход между сечениями)</div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Профили</span>
                <span className={ready ? cl.profileBadgeActive : cl.profileBadgeNeed}>
                    {profileRefs.length ? `выбрано: ${profileRefs.length}` : 'кликни ≥2 профиля'}
                </span>
            </div>
            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Выбери 2+ профиля на РАЗНЫХ плоскостях (напр. круг на XY и
                квадрат на плоскости со смещением) — получится плавный переход.
            </div>
            <div className={cl.formRow}>
                <span className={cl.formLabel}>Операция</span>
                <select className={cl.select} value={operation}
                    onChange={(e) => updatePendingFeature({ operation: e.target.value })}>
                    <option value="new">Новое тело</option>
                    <option value="add">Объединить</option>
                    <option value="cut">Вырезать</option>
                </select>
            </div>
            <div className={cl.formActions}>
                <button className={cl.btnPrimary} disabled={!ready || busy} onClick={onOk}>
                    {busy ? 'Строю…' : 'OK'}
                </button>
                <button className={cl.btnSecondary} onClick={() => { setPendingFeature(null); setSelection(null, []); }}>Отмена</button>
            </div>
        </div>
    );
};


const CombineForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);

    // Какой слот сейчас «активен» — куда уходит следующий клик по телу.
    // 'target' | 'tool'
    const [activeSlot, setActiveSlot] = React.useState('target');

    // Когда юзер кликает по телу в сцене (selection.kind='body'), кладём в активный слот.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'combine') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const bodyId = selection.ids[selection.ids.length - 1]; // последнее выделенное
        if (activeSlot === 'target') {
            if (pendingFeature.targetId !== bodyId) {
                updatePendingFeature({ targetId: bodyId });
                setActiveSlot('tool');
            }
        } else {
            if (pendingFeature.toolId !== bodyId) {
                updatePendingFeature({ toolId: bodyId });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'combine') return null;

    const { targetId, toolId, operation, keepTool } = pendingFeature;
    const ready = !!targetId && !!toolId && targetId !== toolId;
    const allBodies = Array.from(bodyMeshes.keys());

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!ready) {
            toast('Выбери два разных тела (target и tool)', 'error');
            return;
        }
        const client = getGeometryClient();
        try {
            // Сохраняем индексы ДО combine (после combine target/tool исчезнут)
            const targetIdx = getBodyIndex(targetId);
            const toolIdx = getBodyIndex(toolId);
            await client.combineBodies({ targetId, toolId, operation, keepTool });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'combine',
                targetId, toolId,
                // Индексы для rebuild — переживают clearAll
                targetIdx, toolIdx,
                operation, keepTool,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[CombineForm] combine failed:', e);
            toast('Ошибка операции: ' + e.message, 'error');
            return;
        }
        setPendingFeature(null);
        setSelection(null, []);
    });

    const slotBtn = (slot, id) => {
        const isActive = activeSlot === slot;
        return (
            <button
                className={`${cl.profileBadge} ${isActive ? cl.profileBadgeActive : ''}`}
                onClick={() => setActiveSlot(slot)}
                style={{ cursor: 'pointer', width: '100%', textAlign: 'left' }}
            >
                {id ? `✓ ${id}` : (isActive ? 'Кликни по телу в сцене' : 'Не выбрано')}
            </button>
        );
    };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.modify.combine}</div>

            <FormRow label="Целевое тело">
                {slotBtn('target', targetId)}
            </FormRow>
            <FormRow label="Инструмент">
                {slotBtn('tool', toolId)}
            </FormRow>

            {allBodies.length > 0 && (
                <div style={{ fontSize: 11, color: '#888', padding: '4px 0 8px' }}>
                    Тел в документе: {allBodies.length}. Кликни по телу в 3D-окне,
                    чтобы заполнить активный слот.
                </div>
            )}

            <FormRow label={RU.operationParams.operation}>
                <select
                    className={cl.select}
                    value={operation}
                    onChange={(e) => updatePendingFeature({ operation: e.target.value })}
                >
                    <option value="add">{RU.operationParams.opJoin}</option>
                    <option value="cut">{RU.operationParams.opCut}</option>
                    <option value="intersect">{RU.operationParams.opIntersect}</option>
                </select>
            </FormRow>

            <FormRow label="Сохранить инструмент">
                <input
                    type="checkbox"
                    checked={!!keepTool}
                    onChange={(e) => updatePendingFeature({ keepTool: e.target.checked })}
                />
                <span style={{ fontSize: 12, color: '#666', marginLeft: 8 }}>
                    оставить tool как отдельное тело
                </span>
            </FormRow>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!ready}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === MoveCopyForm — переместить / скопировать тело =================
// =====================================================================
//
// pendingFeature {kind:'moveCopy', bodyId, dx, dy, dz, rx, ry, rz, copy}
//
// Юзер выбрал тело (или ещё не выбрал — выбирает кликом в дереве/сцене).
// Меняет dx/dy/dz/rx/ry/rz через inputs или drag-стрелки в 3D —
// MoveCopyPreview слушает pendingFeature и обновляет тело live через
// throttle-by-inflight (так же как fillet).
//
// При copy=true финальный commit создаёт новое тело; оригинал остаётся.
// При copy=false тело перемещается на месте.

const MoveCopyForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);

    // Если тело не выбрано — следим за selection и подставляем первое body.
    // Также фиксируем initialCenter (bbox-центр body на момент старта),
    // чтобы TransformGizmo не дёргался во время live-preview.
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'moveCopy') return;
        if (pendingFeature.bodyId) return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const newId = selection.ids[0];
        const mesh = bodyMeshes.get(newId);
        let initialCenter = [0, 0, 0];
        if (mesh && mesh.positions && mesh.positions.length >= 3) {
            const p = mesh.positions;
            let minX = Infinity, minY = Infinity, minZ = Infinity;
            let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
            for (let i = 0; i < p.length; i += 3) {
                if (p[i] < minX) minX = p[i]; if (p[i] > maxX) maxX = p[i];
                if (p[i+1] < minY) minY = p[i+1]; if (p[i+1] > maxY) maxY = p[i+1];
                if (p[i+2] < minZ) minZ = p[i+2]; if (p[i+2] > maxZ) maxZ = p[i+2];
            }
            initialCenter = [(minX+maxX)/2, (minY+maxY)/2, (minZ+maxZ)/2];
        }
        updatePendingFeature({ bodyId: newId, initialCenter });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    // === Live-preview ===
    // Throttle-by-inflight (как у fillet'а): следим за previewTick из 3D-drag
    // или dx/dy/dz/rx/ry/rz/copy изменений. При изменении запускаем preview.
    const liveInflightRef = React.useRef(false);
    const livePendingRef = React.useRef(null);
    const liveBodyIdRef = React.useRef(null);

    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'moveCopy') return;
        if (!pendingFeature.bodyId) return;
        const args = {
            bodyId: pendingFeature.bodyId,
            dx: pendingFeature.dx || 0,
            dy: pendingFeature.dy || 0,
            dz: pendingFeature.dz || 0,
            rx: pendingFeature.rx || 0,
            ry: pendingFeature.ry || 0,
            rz: pendingFeature.rz || 0,
            copy: !!pendingFeature.copy,
        };
        liveBodyIdRef.current = args.bodyId;
        if (liveInflightRef.current) {
            livePendingRef.current = args;
            return;
        }
        const runOne = async (a) => {
            liveInflightRef.current = true;
            try {
                const client = getGeometryClient();
                await client.moveOrCopyBody({ ...a, preview: true });
            } catch (e) {
                console.warn('[MoveCopyForm] live-preview failed:', e?.message || e);
            } finally {
                liveInflightRef.current = false;
                const next = livePendingRef.current;
                livePendingRef.current = null;
                if (next) runOne(next);
            }
        };
        runOne(args);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        pendingFeature?.bodyId,
        pendingFeature?.dx, pendingFeature?.dy, pendingFeature?.dz,
        pendingFeature?.rx, pendingFeature?.ry, pendingFeature?.rz,
        pendingFeature?.copy,
    ]);

    if (!pendingFeature || pendingFeature.kind !== 'moveCopy') return null;
    const { bodyId, dx, dy, dz, rx, ry, rz, copy } = pendingFeature;
    const hasBody = !!bodyId;

    const onCancel = () => {
        if (liveBodyIdRef.current) {
            try { getGeometryClient().cancelMoveCopyPreview(liveBodyIdRef.current); } catch (_) {}
            liveBodyIdRef.current = null;
        }
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasBody) {
            toast('Сначала выбери тело', 'error');
            return;
        }
        const client = getGeometryClient();
        try {
            await client.moveOrCopyBody({
                bodyId, dx, dy, dz, rx, ry, rz, copy,
                preview: false,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'moveCopy',
                bodyId, dx, dy, dz, rx, ry, rz, copy,
                targetBodyIdx: getBodyIndex(bodyId),
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[MoveCopyForm] failed:', e);
            toast('Ошибка операции: ' + (e?.message || e), 'error');
            return;
        }
        liveBodyIdRef.current = null;
        setPendingFeature(null);
        setSelection(null, []);
    });

    /*
     * Смещение и поворот принимают ФОРМУЛЫ.
     *
     * В сборке это нужнее всего: «сдвинуть на толщина + зазор», «повернуть
     * на 360 / зубьев». Раньше сюда можно было вписать только число, и связь
     * с параметром терялась — при изменении толщины детали приходилось
     * пересчитывать смещения руками.
     *
     * Отрицательные значения разрешены (min не задаём): смещение вполне
     * может быть в минус, в отличие от толщины или радиуса.
     */
    const numInput = (label, key, value, unit = 'мм') => (
        <FormRow label={label}>
            <ExprInput
                value={pendingFeature[`${key}Expr`] ?? value}
                unit={unit}
                onChange={(v, expr) => {
                    updatePendingFeature({ [key]: v, [`${key}Expr`]: expr });
                }}
            />
        </FormRow>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>
                ↔ {RU.modify.moveCopy}
            </div>

            <FormRow label="Тело">
                {hasBody ? (
                    <span className={cl.profileBadge}>✓ {bodyId}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>Кликни по телу в дереве или сцене</span>
                )}
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '6px 0 2px', fontWeight: 600 }}>
                Смещение
            </div>
            {numInput('X', 'dx', dx)}
            {numInput('Y', 'dy', dy)}
            {numInput('Z', 'dz', dz)}

            <div style={{ fontSize: 11, color: '#888', padding: '8px 0 2px', fontWeight: 600 }}>
                Поворот
            </div>
            {numInput('Rx', 'rx', rx, '°')}
            {numInput('Ry', 'ry', ry, '°')}
            {numInput('Rz', 'rz', rz, '°')}

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasBody}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === ShellForm — оболочка (превратить тело в стенки) ===============
// =====================================================================
//
// pendingFeature {kind:'shell', thickness, faces:[{bodyId, faceId, center}]}
//
// Юзер выбрал тело, нажал «Оболочка», кликает по граням которые надо открыть.
// Live-preview через worker.shellBody({preview:true}). При commit (OK)
// preview:false освобождает backup.

const ShellForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const selectedFaceData = useCadStore((s) => s.selectedFaceData);
    const addFeature = useCadStore((s) => s.addFeature);

    // Когда юзер кликает по грани (selection.kind='face' + selectedFaceData),
    // добавляем её к faces (если не дубль).
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'shell') return;
        if (selection.kind !== 'face' || !selectedFaceData?.planeRef?.center) return;
        const refKey = `${selectedFaceData.bodyId}/${selectedFaceData.faceId}`;
        const cur = pendingFeature.faces || [];
        if (cur.some((f) => `${f.bodyId}/${f.faceId}` === refKey)) return;
        // Все выбранные грани должны быть из одного body — replicad shell
        // работает на одном shape за раз.
        if (cur.length > 0 && cur[0].bodyId !== selectedFaceData.bodyId) return;
        // Бампаем previewTick — клик по грани = явное user-action,
        // запускает live-preview через эффект ниже.
        const tick = (pendingFeature.previewTick || 0) + 1;
        updatePendingFeature({
            faces: [...cur, {
                bodyId: selectedFaceData.bodyId,
                faceId: selectedFaceData.faceId,
                center: selectedFaceData.planeRef.center,
            }],
            previewTick: tick,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selectedFaceData?.bodyId, selectedFaceData?.faceId]);

    // Live-preview через throttle-by-inflight (как у fillet/move)
    const liveInflightRef = React.useRef(false);
    const livePendingRef = React.useRef(null);
    const liveBodyIdRef = React.useRef(null);
    const previewTick = pendingFeature?.previewTick;

    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'shell') return;
        const faces = pendingFeature.faces || [];
        if (faces.length === 0) return;
        if (!previewTick) return; // запускаем preview только после явного юзер-action
        const t = Number(pendingFeature.thickness);
        if (!Number.isFinite(t) || Math.abs(t) < 1e-4) return;
        const bodyId = faces[0].bodyId;
        const args = {
            bodyId,
            thickness: t,
            faceCenters: faces.map((f) => f.center),
        };
        liveBodyIdRef.current = bodyId;
        if (liveInflightRef.current) {
            livePendingRef.current = args;
            return;
        }
        const runOne = async (a) => {
            liveInflightRef.current = true;
            try {
                await getGeometryClient().shellBody({ ...a, preview: true });
            } catch (e) {
                console.warn('[ShellForm] live-preview failed:', e?.message || e);
            } finally {
                liveInflightRef.current = false;
                const next = livePendingRef.current;
                livePendingRef.current = null;
                if (next) runOne(next);
            }
        };
        runOne(args);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewTick, pendingFeature]);

    if (!pendingFeature || pendingFeature.kind !== 'shell') return null;
    const { thickness, faces = [] } = pendingFeature;
    const hasFaces = faces.length > 0;

    const onCancel = () => {
        if (liveBodyIdRef.current) {
            try { getGeometryClient().cancelShellPreview(liveBodyIdRef.current); } catch (_) {}
            liveBodyIdRef.current = null;
        }
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onClearFaces = () => {
        // Сначала откатываем preview, потом чистим выбор
        if (liveBodyIdRef.current) {
            try { getGeometryClient().cancelShellPreview(liveBodyIdRef.current); } catch (_) {}
            liveBodyIdRef.current = null;
        }
        updatePendingFeature({ faces: [], previewTick: 0 });
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasFaces) {
            toast('Выбери хотя бы одну грань (кликни по поверхности тела)', 'error');
            return;
        }
        const t = Number(thickness);
        if (!Number.isFinite(t) || Math.abs(t) < 1e-4) {
            toast('Толщина должна быть ненулевая', 'error');
            return;
        }
        const bodyId = faces[0].bodyId;
        try {
            await getGeometryClient().shellBody({
                bodyId,
                thickness: t,
                faceCenters: faces.map((f) => f.center),
                preview: false,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind: 'shell',
                bodyId,
                targetBodyIdx: getBodyIndex(bodyId),
                thickness: t,
                faces: [...faces],
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[ShellForm] failed:', e);
            toast('Ошибка оболочки: ' + (e?.message || e), 'error');
            return;
        }
        liveBodyIdRef.current = null;
        setPendingFeature(null);
        setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.modify.shell}</div>

            <FormRow label="Открытые грани">
                {hasFaces ? (
                    <span className={cl.profileBadge}>
                        ✓ Выбрано: {faces.length}
                    </span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по граням тела (которые надо удалить)
                    </span>
                )}
            </FormRow>

            {hasFaces && (
                <button
                    className={`${cl.btn} ${cl.btnSecondary}`}
                    onClick={onClearFaces}
                    style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 11 }}
                >
                    Сбросить выбор
                </button>
            )}

            <FormRow label="Толщина стенки">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        className={cl.numInput}
                        value={thickness}
                        onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            const tick = (pendingFeature.previewTick || 0) + 1;
                            updatePendingFeature({
                                thickness: isNaN(v) ? 0 : v,
                                previewTick: tick,
                            });
                        }}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Положительная толщина = стенки наружу, отрицательная = внутрь.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasFaces}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === FilletChamferForm — скругление / фаска по рёбрам ==============
// =====================================================================
//
// pendingFeature {kind:'fillet'|'chamfer', edgeRefs: ['bodyId/edgeId', ...], radius}
//
// Юзер кликает по рёбрам в 3D-сцене → store.setSelection('edge', ids).
// Этот компонент следит за selection и копирует выбор в pendingFeature.edgeRefs.
// (Делать через selection напрямую нельзя — после OK очищаем selection,
//  а edgeRefs нужно сохранить в Feature.)
//
// Edit-mode: загрузили существующую фичу, edgeRefs заполнены — slушаем
// дополнительные клики и расширяем список.

const FilletChamferForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);

    // Синхронизируем selection (kind='edge') → pendingFeature.edgeRefs.
    // НЕ бампаем previewTick — это не считается «взаимодействием юзера со
    // стрелкой/инпутом», поэтому live-preview сам по себе не запустится.
    // Юзер должен потянуть стрелку или ввести значение чтобы applied.
    React.useEffect(() => {
        if (!pendingFeature) return;
        if (pendingFeature.kind !== 'fillet' && pendingFeature.kind !== 'chamfer') return;
        if (selection.kind !== 'edge') return;
        const cur = pendingFeature.edgeRefs || [];
        const sel = selection.ids;
        if (cur.length === sel.length && cur.every((x, i) => x === sel[i])) return;
        // Снимаем дескрипторы рёбер СЕЙЧАС — пока тело ОРИГИНАЛЬНОЕ (до preview).
        // При commit тело в bodyMeshes уже превью-скруглённое, и пересчёт
        // дескрипторов дал бы неверные значения → ребро «не находится»
        // (баг фаски на теле со скруглением). Группируем по bodyId.
        const byBody = new Map();
        for (const ref of sel) {
            const [bid, eidStr] = ref.split('/');
            if (!byBody.has(bid)) byBody.set(bid, []);
            byBody.get(bid).push(parseInt(eidStr, 10));
        }
        const savedDescs = [];
        for (const [bid, eids] of byBody.entries()) {
            savedDescs.push(...getEdgeDescriptorsForBody(bid, eids));
        }
        // Сбрасываем previewTick — новый набор рёбер, прежний preview невалиден
        updatePendingFeature({ edgeRefs: [...sel], edgeDescs: savedDescs, previewTick: 0 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    // === Live-preview ===
    // Запускается ТОЛЬКО когда юзер реально взаимодействует со стрелкой
    // (drag) или вписывает значение в input — иначе при выборе ребра
    // скругление сразу применилось бы и стрелка перестала бы работать.
    //
    // Триггер — поле pendingFeature.previewTick (счётчик), которое
    // инкрементируется в FilletPreview при drag и в FilletDragInput при
    // вводе значения. useEffect срабатывает на изменение тика → запускает
    // fillet с текущим radius.
    //
    // Throttle-by-inflight: если предыдущий запрос ещё в полёте, запоминаем
    // что есть «отложенный» вызов с актуальными параметрами, и запустим его
    // когда воркер освободится. Это даёт плавное обновление при drag.
    const liveInflightRef = React.useRef(false);
    const livePendingRef = React.useRef(null);
    const liveBodyIdRef = React.useRef(null);
    const lastPreviewToastRef = React.useRef(0);
    const lastPreviewOkRef = React.useRef(true);
    const isFilletKind = pendingFeature?.kind === 'fillet' || pendingFeature?.kind === 'chamfer';
    const previewTick = pendingFeature?.previewTick;

    React.useEffect(() => {
        if (!isFilletKind) return;
        if (!previewTick) return;  // юзер ещё не взаимодействовал
        if (!pendingFeature.edgeRefs?.length) return;
        if (!(pendingFeature.radius > 0)) return;
        if (pendingFeature.editingFeatureId) return;

        // Группируем edgeRefs по bodyId
        const byBody = new Map();
        for (const ref of pendingFeature.edgeRefs) {
            const [bodyId, edgeIdStr] = ref.split('/');
            const eid = parseInt(edgeIdStr, 10);
            if (!byBody.has(bodyId)) byBody.set(bodyId, []);
            byBody.get(bodyId).push(eid);
        }
        if (byBody.size !== 1) return;
        const [bodyId, edgeIds] = [...byBody.entries()][0];
        const args = {
            bodyId,
            edgeIds,
            // Дескрипторы, снятые при выборе ребра (тело было оригинальным).
            // НЕ пересчитываем из текущего меша — он уже превью-скруглён.
            // Проверяем .length (пустой массив truthy → нельзя `|| fallback`).
            edgeDescs: (pendingFeature.edgeDescs && pendingFeature.edgeDescs.length)
                ? pendingFeature.edgeDescs : getEdgeDescriptorsForBody(bodyId, edgeIds),
            radius: pendingFeature.radius,
            kind: pendingFeature.kind,
        };
        liveBodyIdRef.current = bodyId;

        if (liveInflightRef.current) {
            livePendingRef.current = args;
            return;
        }

        const runOne = async (a) => {
            liveInflightRef.current = true;
            try {
                const client = getGeometryClient();
                await client.filletOrChamfer({ ...a, preview: true });
                lastPreviewOkRef.current = true;
            } catch (e) {
                console.warn('[FilletChamferForm] live-preview failed:', e?.message || e);
                // Показываем причину (с дебаунсом — не спамим при drag).
                const now = Date.now();
                if (now - (lastPreviewToastRef.current || 0) > 2500) {
                    lastPreviewToastRef.current = now;
                    lastPreviewOkRef.current = false;
                    toast((a.kind === 'fillet' ? 'Скругление' : 'Фаска')
                        + ' не строится: ' + (e?.message || e), 'error');
                }
            } finally {
                liveInflightRef.current = false;
                const next = livePendingRef.current;
                livePendingRef.current = null;
                if (next) runOne(next);
            }
        };
        runOne(args);
    }, [previewTick, isFilletKind, pendingFeature?.editingFeatureId, pendingFeature]);

    if (!pendingFeature) return null;
    if (pendingFeature.kind !== 'fillet' && pendingFeature.kind !== 'chamfer') return null;

    const { kind, edgeRefs, radius, editingFeatureId } = pendingFeature;
    const isFillet = kind === 'fillet';
    const isEditing = !!editingFeatureId;
    const hasEdges = edgeRefs && edgeRefs.length > 0;

    const onCancel = () => {
        // Откатываем preview если был
        if (liveBodyIdRef.current) {
            try {
                getGeometryClient().cancelFilletPreview(liveBodyIdRef.current);
            } catch (_) {}
            liveBodyIdRef.current = null;
        }
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasEdges) {
            toast('Выбери хотя бы одно ребро (кликни по линии тела)', 'error');
            return;
        }
        const r = Number(radius);
        if (!Number.isFinite(r) || r <= 0) {
            toast(isFillet ? 'Радиус должен быть больше 0' : 'Размер фаски должен быть больше 0', 'error');
            return;
        }

        // Группируем edgeRefs по bodyId — но replicad fillet применяется
        // к одному shape. Для MVP требуем все рёбра из ОДНОГО тела.
        const byBody = new Map();
        for (const ref of edgeRefs) {
            const [bodyId, edgeIdStr] = ref.split('/');
            const eid = parseInt(edgeIdStr, 10);
            if (!byBody.has(bodyId)) byBody.set(bodyId, []);
            byBody.get(bodyId).push(eid);
        }
        if (byBody.size > 1) {
            toast('Все выбранные рёбра должны принадлежать одному телу', 'error');
            return;
        }
        const [bodyId, edgeIds] = [...byBody.entries()][0];

        if (isEditing) {
            // ВАЖНО: пересчитываем дескрипторы рёбер перед сохранением — иначе при
            // следующем rebuild старые координаты могут не совпасть с новыми
            // рёбрами (если pipeline после редактирования изменил геометрию).
            // edgeDescs (mid+len) — основной механизм; edgeGeoms — совместимость.
            const edgeDescs = getEdgeDescriptorsForBody(bodyId, edgeIds);
            const edgeGeoms = getEdgeEndpointsForBody(bodyId, edgeIds);
            updateFeature(editingFeatureId, {
                edgeRefs: [...edgeRefs],
                edgeDescs,
                edgeGeoms,
                radius: r,
                targetBodyIdx: getBodyIndex(bodyId),
            });
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[FilletChamferForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + e.message, 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        const client = getGeometryClient();
        // Дескрипторы, снятые при ВЫБОРЕ ребра (тело было оригинальным). Если
        // пересчитать сейчас — тело в bodyMeshes уже превью-скруглённое, и ребро
        // «не найдётся» (баг фаски на теле со скруглением). Fallback — пересчёт.
        const edgeDescs = (pendingFeature.edgeDescs && pendingFeature.edgeDescs.length)
            ? pendingFeature.edgeDescs
            : getEdgeDescriptorsForBody(bodyId, edgeIds);
        const edgeGeoms = getEdgeEndpointsForBody(bodyId, edgeIds);
        try {
            // Финальный commit: preview=false → worker освобождает backup,
            // тело становится постоянным.
            await client.filletOrChamfer({
                bodyId,
                edgeIds,
                edgeDescs,
                radius: r,
                kind,
                preview: false,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                kind,
                edgeRefs: [...edgeRefs],
                edgeDescs,
                edgeGeoms,
                radius: r,
                targetBodyIdx: getBodyIndex(bodyId),
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[FilletChamferForm] failed:', e);
            toast((isFillet ? 'Ошибка скругления: ' : 'Ошибка фаски: ') + e.message, 'error');
            return;
        }
        liveBodyIdRef.current = null;
        setPendingFeature(null);
        setSelection(null, []);
    });

    const onClearEdges = () => {
        updatePendingFeature({ edgeRefs: [] });
        setSelection(null, []);
    };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>
                
                {isEditing
                    ? (isFillet ? 'Изменить скругление' : 'Изменить фаску')
                    : (isFillet ? RU.modify.fillet : RU.modify.chamfer)}
            </div>

            <FormRow label={RU.operationParams.edges}>
                {hasEdges ? (
                    <span className={cl.profileBadge}>
                        ✓ Выбрано рёбер: {edgeRefs.length}
                    </span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по рёбрам тела (можно с Shift)
                    </span>
                )}
            </FormRow>

            {hasEdges && (
                <button
                    className={`${cl.btn} ${cl.btnSecondary}`}
                    onClick={onClearEdges}
                    style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 11 }}
                >
                    Сбросить выбор
                </button>
            )}

            <FormRow label={isFillet ? RU.operationParams.radius : 'Размер фаски'}>
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        min="0.1"
                        className={cl.numInput}
                        value={radius}
                        onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            // Бамп previewTick — триггер для live-preview useEffect.
                            const tick = (pendingFeature.previewTick || 0) + 1;
                            updatePendingFeature({
                                radius: isNaN(v) ? 0 : v,
                                previewTick: tick,
                            });
                        }}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasEdges}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === MeasureForm — инструмент измерения =============================
// =====================================================================
//
// pendingFeature {kind:'measure', mode:'pointToPoint'|'edge',
//                 points:[[x,y,z],...], edge:{...} }
//
// Инструмент анализа: НЕ создаёт геометрию, ничего не пишет в документ.
// Клики ловит Viewport (BodyMesh даёт точку, EdgeLine — ребро).

const MeasureForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);

    if (!pendingFeature || pendingFeature.kind !== 'measure') return null;
    const { mode = 'pointToPoint', points = [], edge } = pendingFeature;

    // Расстояние между двумя точками + дельты по осям.
    let dist = null, deltas = null;
    if (points.length === 2) {
        const dx = points[1][0] - points[0][0];
        const dy = points[1][1] - points[0][1];
        const dz = points[1][2] - points[0][2];
        dist = Math.hypot(dx, dy, dz);
        deltas = [dx, dy, dz];
    }

    const onReset = () => updatePendingFeature({ points: [], edge: null });
    const onClose = () => setPendingFeature(null);
    const setMode = (m) => updatePendingFeature({
        mode: m, points: [], edge: null,
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Измерение</div>

            <FormRow label="Что измеряем">
                <select
                    className={cl.select}
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                >
                    <option value="pointToPoint">Расстояние (2 точки)</option>
                    <option value="edge">Длина ребра</option>
                </select>
            </FormRow>

            {mode === 'pointToPoint' && (
                <>
                    <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                        {points.length === 0 && 'Кликни две точки на поверхности тел.'}
                        {points.length === 1 && 'Первая точка выбрана. Кликни вторую.'}
                        {points.length === 2 && 'Измерено.'}
                    </div>
                    {dist != null && (
                        <div style={{
                            background: 'rgba(232,183,58,0.12)',
                            border: '1px solid #e8b73a44',
                            borderRadius: 4, padding: '8px 10px',
                            margin: '4px 0',
                        }}>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#e8b73a' }}>
                                {dist.toFixed(2)} мм
                            </div>
                            <div style={{ fontSize: 11, color: '#9aa6b3', marginTop: 4 }}>
                                ΔX {deltas[0].toFixed(2)} ·
                                ΔY {deltas[1].toFixed(2)} ·
                                ΔZ {deltas[2].toFixed(2)}
                            </div>
                        </div>
                    )}
                </>
            )}

            {mode === 'edge' && (
                <>
                    <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                        {edge ? 'Измерено.' : 'Кликни ребро тела (тёмная линия).'}
                    </div>
                    {edge && (
                        <div style={{
                            background: 'rgba(232,183,58,0.12)',
                            border: '1px solid #e8b73a44',
                            borderRadius: 4, padding: '8px 10px',
                            margin: '4px 0',
                        }}>
                            <div style={{ fontSize: 15, fontWeight: 800, color: '#e8b73a' }}>
                                {edge.length.toFixed(2)} мм
                            </div>
                            <div style={{ fontSize: 11, color: '#9aa6b3', marginTop: 4 }}>
                                длина ребра
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className={cl.formActions}>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onReset}>
                    Сбросить
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onClose}>
                    Закрыть
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === WorkaxisForm — рабочая ось через 2 точки =======================
// =====================================================================
//
// pendingFeature {kind:'workaxis', p1:[x,y,z], p2:[x,y,z]}
//
// Юзер задаёт две точки координатами. OK создаёт рабочую ось
// (doc.workaxes) — её можно выбрать как ось кругового массива.

const WorkaxisForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addWorkaxis = useCadStore((s) => s.addWorkaxis);

    if (!pendingFeature || pendingFeature.kind !== 'workaxis') return null;
    const p1 = pendingFeature.p1 || [0, 0, 0];
    const p2 = pendingFeature.p2 || [0, 0, 0];

    const onCancel = () => setPendingFeature(null);

    // Обновить одну координату точки p1/p2.
    const setCoord = (which, idx, val) => {
        const cur = (which === 'p1' ? p1 : p2).slice();
        cur[idx] = parseFloat(val) || 0;
        updatePendingFeature({ [which]: cur });
    };

    const onOk = () => {
        const dx = p2[0] - p1[0], dy = p2[1] - p1[1], dz = p2[2] - p1[2];
        if (Math.hypot(dx, dy, dz) < 1e-6) {
            toast('Точки совпадают — ось не определена', 'error');
            return;
        }
        const existing = useCadStore.getState().doc.workaxes || [];
        addWorkaxis({
            id: `wax_${Date.now().toString(36)}`,
            p1: [...p1], p2: [...p2],
            name: `Ось ${existing.length + 1}`,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
    };

    // Тройка полей X/Y/Z для одной точки.
    const pointRow = (label, which, pt) => (
        <FormRow label={label}>
            <div style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map((i) => (
                    <input
                        key={i}
                        type="number"
                        step="any"
                        className={cl.numInput}
                        style={{ width: 52 }}
                        value={pt[i]}
                        title={['X', 'Y', 'Z'][i]}
                        onChange={(e) => setCoord(which, i, e.target.value)}
                    />
                ))}
            </div>
        </FormRow>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Рабочая ось</div>

            {pointRow('Точка 1 (X Y Z)', 'p1', p1)}
            {pointRow('Точка 2 (X Y Z)', 'p2', p2)}

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Ось проходит через две точки. Её можно выбрать как ось
                вращения для кругового массива.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === OffsetPlaneForm — рабочая плоскость со смещением ===============
// =====================================================================
//
// pendingFeature {kind:'offsetPlane', basePlane:'XY'|'XZ'|'YZ', offset}
//
// Юзер нажал «Плоскость со смещением», выбирает базовую плоскость и
// смещение. OK создаёт рабочую плоскость (doc.workplanes) — она видна
// в дереве, на ней можно создавать эскизы.

const OffsetPlaneForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addWorkplane = useCadStore((s) => s.addWorkplane);

    if (!pendingFeature || pendingFeature.kind !== 'offsetPlane') return null;
    const { basePlane = 'XY', offset = 0 } = pendingFeature;

    const onCancel = () => setPendingFeature(null);

    const onOk = () => {
        const off = Number(offset);
        if (!Number.isFinite(off)) {
            toast('Смещение должно быть числом', 'error');
            return;
        }
        const existing = useCadStore.getState().doc.workplanes || [];
        addWorkplane({
            id: `wp_${Date.now().toString(36)}`,
            basePlane,
            offset: off,
            name: `Плоскость ${existing.length + 1}`,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
    };

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Плоскость со смещением</div>

            <FormRow label="Базовая плоскость">
                <select
                    className={cl.select}
                    value={basePlane}
                    onChange={(e) => updatePendingFeature({ basePlane: e.target.value })}
                >
                    <option value="XY">XY (горизонтальная)</option>
                    <option value="XZ">XZ (фронтальная)</option>
                    <option value="YZ">YZ (профильная)</option>
                </select>
            </FormRow>

            <FormRow label="Смещение">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        className={cl.numInput}
                        value={offset}
                        onChange={(e) => updatePendingFeature({
                            offset: parseFloat(e.target.value) || 0,
                        })}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Новая плоскость появится в дереве («Вспомогательная
                геометрия»). На ней можно создавать эскизы.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// === AngledPlaneForm — плоскость под углом к базовой ===
const AngledPlaneForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addWorkplane = useCadStore((s) => s.addWorkplane);
    if (!pendingFeature || pendingFeature.kind !== 'planeAtAngle') return null;
    const { basePlane = 'XY', rotAxis = 'X', angle = 45 } = pendingFeature;

    const onOk = () => {
        const a = Number(angle);
        if (!Number.isFinite(a)) { toast('Угол должен быть числом', 'error'); return; }
        const existing = useCadStore.getState().doc.workplanes || [];
        addWorkplane({
            id: `wp_${Date.now().toString(36)}`,
            kind: 'angle', basePlane, rotAxis, angle: a,
            name: `Плоскость под углом ${existing.length + 1}`,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
    };
    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Плоскость под углом</div>
            <FormRow label="Базовая плоскость">
                <select className={cl.select} value={basePlane}
                    onChange={(e) => updatePendingFeature({ basePlane: e.target.value })}>
                    <option value="XY">XY (горизонтальная)</option>
                    <option value="XZ">XZ (фронтальная)</option>
                    <option value="YZ">YZ (профильная)</option>
                </select>
            </FormRow>
            <FormRow label="Ось наклона">
                <select className={cl.select} value={rotAxis}
                    onChange={(e) => updatePendingFeature({ rotAxis: e.target.value })}>
                    <option value="X">Горизонтальная (X плоскости)</option>
                    <option value="Y">Вертикальная (Y плоскости)</option>
                </select>
            </FormRow>
            <FormRow label="Угол">
                <div className={cl.inputWithUnit}>
                    <input type="number" step="any" className={cl.numInput} value={angle}
                        onChange={(e) => updatePendingFeature({ angle: parseFloat(e.target.value) || 0 })}/>
                    <span className={cl.unit}>°</span>
                </div>
            </FormRow>
            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Плоскость проходит через начало координат под заданным углом.
                На ней можно создавать эскизы.
            </div>
            <div className={cl.formActions}>
                <button className={`${cl.btn} ${cl.btnPrimary}`} onClick={onOk}>OK</button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={() => setPendingFeature(null)}>Отмена</button>
            </div>
        </div>
    );
};


// === MidplaneForm — срединная плоскость между двумя смещениями ===
const MidplaneForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addWorkplane = useCadStore((s) => s.addWorkplane);
    if (!pendingFeature || pendingFeature.kind !== 'midplane') return null;
    const { basePlane = 'XY', offset1 = 0, offset2 = 40 } = pendingFeature;

    const onOk = () => {
        const o1 = Number(offset1), o2 = Number(offset2);
        if (!Number.isFinite(o1) || !Number.isFinite(o2)) { toast('Смещения должны быть числами', 'error'); return; }
        const existing = useCadStore.getState().doc.workplanes || [];
        addWorkplane({
            id: `wp_${Date.now().toString(36)}`,
            kind: 'midplane', basePlane, offset1: o1, offset2: o2,
            name: `Срединная плоскость ${existing.length + 1}`,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
    };
    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Срединная плоскость</div>
            <FormRow label="Базовая плоскость">
                <select className={cl.select} value={basePlane}
                    onChange={(e) => updatePendingFeature({ basePlane: e.target.value })}>
                    <option value="XY">XY</option><option value="XZ">XZ</option><option value="YZ">YZ</option>
                </select>
            </FormRow>
            <FormRow label="Смещение 1">
                <div className={cl.inputWithUnit}>
                    <input type="number" step="any" className={cl.numInput} value={offset1}
                        onChange={(e) => updatePendingFeature({ offset1: parseFloat(e.target.value) || 0 })}/>
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>
            <FormRow label="Смещение 2">
                <div className={cl.inputWithUnit}>
                    <input type="number" step="any" className={cl.numInput} value={offset2}
                        onChange={(e) => updatePendingFeature({ offset2: parseFloat(e.target.value) || 0 })}/>
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>
            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Плоскость появится ровно посередине между двумя смещениями
                ({((Number(offset1) + Number(offset2)) / 2).toFixed(1)} мм).
            </div>
            <div className={cl.formActions}>
                <button className={`${cl.btn} ${cl.btnPrimary}`} onClick={onOk}>OK</button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={() => setPendingFeature(null)}>Отмена</button>
            </div>
        </div>
    );
};


// === PlaneThrough3Form — плоскость через 3 точки ===
const PlaneThrough3Form = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const addWorkplane = useCadStore((s) => s.addWorkplane);
    if (!pendingFeature || pendingFeature.kind !== 'planeThroughThree') return null;
    const p1 = pendingFeature.p1 || [0, 0, 0];
    const p2 = pendingFeature.p2 || [50, 0, 0];
    const p3 = pendingFeature.p3 || [0, 50, 20];

    const setCoord = (which, axis, val) => {
        const arr = [...pendingFeature[which]];
        arr[axis] = parseFloat(val) || 0;
        updatePendingFeature({ [which]: arr });
    };

    const onOk = () => {
        // Проверка: точки не коллинеарны (нормаль ненулевая).
        const v1 = [p2[0]-p1[0], p2[1]-p1[1], p2[2]-p1[2]];
        const v2 = [p3[0]-p1[0], p3[1]-p1[1], p3[2]-p1[2]];
        const nx = v1[1]*v2[2]-v1[2]*v2[1], ny = v1[2]*v2[0]-v1[0]*v2[2], nz = v1[0]*v2[1]-v1[1]*v2[0];
        if (Math.hypot(nx, ny, nz) < 1e-6) {
            toast('Точки на одной прямой — плоскость не определить', 'error'); return;
        }
        const existing = useCadStore.getState().doc.workplanes || [];
        addWorkplane({
            id: `wp_${Date.now().toString(36)}`,
            kind: 'three_points', p1, p2, p3,
            name: `Плоскость через 3 точки ${existing.length + 1}`,
            createdAt: Date.now(),
        });
        setPendingFeature(null);
    };

    const ptRow = (label, which, arr) => (
        <div className={cl.formRow}>
            <span className={cl.formLabel}>{label}</span>
            <span style={{ display: 'flex', gap: 4 }}>
                {[0, 1, 2].map((ax) => (
                    <input key={ax} type="number" step="any" className={cl.numInput}
                        style={{ width: 52 }} value={arr[ax]}
                        onChange={(e) => setCoord(which, ax, e.target.value)}/>
                ))}
            </span>
        </div>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>Плоскость через 3 точки</div>
            {ptRow('Точка 1 (X Y Z)', 'p1', p1)}
            {ptRow('Точка 2 (X Y Z)', 'p2', p2)}
            {ptRow('Точка 3 (X Y Z)', 'p3', p3)}
            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Плоскость пройдёт через три заданные точки. На ней можно
                создавать эскизы.
            </div>
            <div className={cl.formActions}>
                <button className={`${cl.btn} ${cl.btnPrimary}`} onClick={onOk}>OK</button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={() => setPendingFeature(null)}>Отмена</button>
            </div>
        </div>
    );
};


// =====================================================================
// === CircPatternForm — круговой массив (N копий по окружности) ======
// =====================================================================
//
// pendingFeature {kind:'circPattern', bodyId, axis:'X'|'Y'|'Z',
//                 count, angle, editingFeatureId? }
//
// Юзер нажал «Круговой массив», выбирает тело, задаёт ось вращения,
// число копий и угол охвата. OK расставляет копии по окружности.

const CircPatternForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);
    const workaxes = useCadStore((s) => s.doc.workaxes || []);

    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'circPattern') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const bodyId = selection.ids[selection.ids.length - 1];
        if (pendingFeature.bodyId !== bodyId) {
            updatePendingFeature({ bodyId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'circPattern') return null;
    const {
        bodyId, axis = 'Z', count = 1, angle = 360, editingFeatureId,
    } = pendingFeature;
    const hasBody = !!bodyId;

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasBody) {
            toast('Выбери тело (кликни по нему в 3D-окне)', 'error');
            return;
        }
        const n = Math.round(count);
        if (n < 2) {
            toast('Нужно минимум 2 копии', 'error');
            return;
        }
        const ang = Number(angle);
        if (!Number.isFinite(ang) || ang <= 0 || ang > 360) {
            toast('Угол должен быть в диапазоне 1…360°', 'error');
            return;
        }
        const featureData = {
            kind: 'circPattern',
            bodyId,
            axis: axis || 'Z',
            count: n,
            angle: ang,
            sourceBodyIdx: getBodyIndex(bodyId),
        };
        // Если выбрана рабочая ось (id вида wax_*) — добавляем её
        // геометрию: точку на оси (p1) и направление (p2 - p1).
        const wax = workaxes.find((a) => a.id === axis);
        if (wax) {
            featureData.axisOrigin = [...wax.p1];
            featureData.axisDir = [
                wax.p2[0] - wax.p1[0],
                wax.p2[1] - wax.p1[1],
                wax.p2[2] - wax.p1[2],
            ];
        }

        if (editingFeatureId) {
            updateFeature(editingFeatureId, featureData);
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[CircPatternForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + (e?.message || e), 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        try {
            await getGeometryClient().circPattern({
                sourceBodyId: bodyId,
                axis: featureData.axis,
                axisOrigin: featureData.axisOrigin,
                axisDir: featureData.axisDir,
                count: n,
                angle: ang,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                ...featureData,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[CircPatternForm] failed:', e);
            toast('Ошибка кругового массива: ' + (e?.message || e), 'error');
            return;
        }
        setPendingFeature(null);
        setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.create.circPattern}</div>

            <FormRow label="Тело">
                {hasBody ? (
                    <span className={cl.profileBadge}>✓ {bodyId}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по телу в 3D-окне
                    </span>
                )}
            </FormRow>

            <FormRow label="Ось вращения">
                <select
                    className={cl.select}
                    value={axis}
                    onChange={(e) => updatePendingFeature({ axis: e.target.value })}
                >
                    <option value="Z">Ось Z (вертикальная)</option>
                    <option value="X">Ось X</option>
                    <option value="Y">Ось Y</option>
                    {workaxes.map((a) => (
                        <option key={a.id} value={a.id}>
                            {a.name || 'Рабочая ось'}
                        </option>
                    ))}
                </select>
            </FormRow>

            <FormRow label="Число копий">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="1"
                        min="2"
                        className={cl.numInput}
                        value={count}
                        onChange={(e) => updatePendingFeature({
                            count: parseInt(e.target.value, 10) || 0,
                        })}
                    />
                    <span className={cl.unit}>шт</span>
                </div>
            </FormRow>

            <FormRow label="Угол охвата">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        min="1"
                        max="360"
                        className={cl.numInput}
                        value={angle}
                        onChange={(e) => updatePendingFeature({
                            angle: parseFloat(e.target.value) || 0,
                        })}
                    />
                    <span className={cl.unit}>°</span>
                </div>
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                {Math.round(count)} копий равномерно по дуге {Math.round(angle)}°
                вокруг оси {axis}. Оригинал входит в число копий.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasBody}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === RectPatternForm — прямоугольный массив (сетка N×M копий) =======
// =====================================================================
//
// pendingFeature {kind:'rectPattern', bodyId, countX, stepX,
//                 countY, stepY, editingFeatureId? }
//
// Юзер нажал «Массив», выбирает тело (клик в сцене), задаёт число
// копий и шаг по X и Y. OK создаёт сетку копий — оригинал остаётся.

const RectPatternForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);

    // Клик по телу в сцене → записываем bodyId.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'rectPattern') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const bodyId = selection.ids[selection.ids.length - 1];
        if (pendingFeature.bodyId !== bodyId) {
            updatePendingFeature({ bodyId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'rectPattern') return null;
    const {
        bodyId, countX = 1, stepX = 0, countY = 1, stepY = 0,
        editingFeatureId,
    } = pendingFeature;
    const hasBody = !!bodyId;
    const total = Math.max(1, Math.round(countX)) * Math.max(1, Math.round(countY));

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasBody) {
            toast('Выбери тело (кликни по нему в 3D-окне)', 'error');
            return;
        }
        const nx = Math.round(countX), ny = Math.round(countY);
        if (nx < 1 || ny < 1) {
            toast('Число копий должно быть не меньше 1', 'error');
            return;
        }
        if (nx === 1 && ny === 1) {
            toast('Массив 1×1 ничего не создаёт — увеличь число копий', 'error');
            return;
        }
        const featureData = {
            kind: 'rectPattern',
            bodyId,
            countX: nx, stepX: Number(stepX) || 0,
            countY: ny, stepY: Number(stepY) || 0,
            sourceBodyIdx: getBodyIndex(bodyId),
        };

        if (editingFeatureId) {
            updateFeature(editingFeatureId, featureData);
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[RectPatternForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + (e?.message || e), 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        try {
            await getGeometryClient().rectPattern({
                sourceBodyId: bodyId,
                countX: nx, stepX: featureData.stepX,
                countY: ny, stepY: featureData.stepY,
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                ...featureData,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[RectPatternForm] failed:', e);
            toast('Ошибка массива: ' + (e?.message || e), 'error');
            return;
        }
        setPendingFeature(null);
        setSelection(null, []);
    });

    const numField = (label, key, value, min) => (
        <FormRow label={label}>
            <div className={cl.inputWithUnit}>
                <input
                    type="number"
                    step="any"
                    min={min}
                    className={cl.numInput}
                    value={value}
                    onChange={(e) => updatePendingFeature({
                        [key]: parseFloat(e.target.value) || 0,
                    })}
                />
                <span className={cl.unit}>{key.startsWith('count') ? 'шт' : 'мм'}</span>
            </div>
        </FormRow>
    );

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.create.pattern}</div>

            <FormRow label="Тело">
                {hasBody ? (
                    <span className={cl.profileBadge}>✓ {bodyId}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по телу в 3D-окне
                    </span>
                )}
            </FormRow>

            {numField('Копий по X', 'countX', countX, 1)}
            {numField('Шаг по X', 'stepX', stepX)}
            {numField('Копий по Y', 'countY', countY, 1)}
            {numField('Шаг по Y', 'stepY', stepY)}

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Сетка {Math.round(countX)}×{Math.round(countY)} — всего {total} тел
                (включая оригинал). Оригинал остаётся на месте.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasBody}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === MirrorForm — зеркальная копия тела относительно плоскости ======
// =====================================================================
//
// pendingFeature {kind:'mirror', bodyId, plane:'XY'|'XZ'|'YZ',
//                 editingFeatureId? }
//
// Юзер нажал «Зеркало», выбирает тело (клик в сцене) и плоскость
// симметрии. OK создаёт зеркальную копию — оригинал остаётся.

const MirrorForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);

    // Клик по телу в сцене (selection.kind='body') → записываем bodyId.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'mirror') return;
        if (selection.kind !== 'body' || selection.ids.length === 0) return;
        const bodyId = selection.ids[selection.ids.length - 1];
        if (pendingFeature.bodyId !== bodyId) {
            updatePendingFeature({ bodyId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selection.ids.join(',')]);

    if (!pendingFeature || pendingFeature.kind !== 'mirror') return null;
    const { bodyId, plane, editingFeatureId } = pendingFeature;
    const hasBody = !!bodyId;

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasBody) {
            toast('Выбери тело (кликни по нему в 3D-окне)', 'error');
            return;
        }
        const featureData = {
            kind: 'mirror',
            bodyId,
            plane: plane || 'YZ',
            sourceBodyIdx: getBodyIndex(bodyId),
        };

        if (editingFeatureId) {
            updateFeature(editingFeatureId, featureData);
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[MirrorForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + (e?.message || e), 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        try {
            await getGeometryClient().mirrorBody({
                sourceBodyId: bodyId,
                plane: plane || 'YZ',
            });
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                ...featureData,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[MirrorForm] failed:', e);
            toast('Ошибка зеркала: ' + (e?.message || e), 'error');
            return;
        }
        setPendingFeature(null);
        setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.create.mirror}</div>

            <FormRow label="Тело">
                {hasBody ? (
                    <span className={cl.profileBadge}>✓ {bodyId}</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по телу в 3D-окне
                    </span>
                )}
            </FormRow>

            <FormRow label="Плоскость симметрии">
                <select
                    className={cl.select}
                    value={plane}
                    onChange={(e) => updatePendingFeature({ plane: e.target.value })}
                >
                    <option value="YZ">YZ (зеркало по X)</option>
                    <option value="XZ">XZ (зеркало по Y)</option>
                    <option value="XY">XY (зеркало по Z)</option>
                </select>
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Создаётся зеркальная копия тела. Оригинал остаётся на месте.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasBody}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


// =====================================================================
// === HoleForm — отверстие (цилиндрический вырез через грань) ========
// =====================================================================
//
// pendingFeature {kind:'hole', faceInfo:{bodyId,faceId,planeRef,faceCenter},
//                 diameter, depth, depthType, offsetX, offsetY,
//                 editingFeatureId? }
//
// Юзер нажал «Отверстие», кликает по грани тела — грань попадает в
// faceInfo. Отверстие сверлится в центре грани; смещение задаётся
// числовыми полями X/Y. Тип: насквозь / на глубину.

const HoleForm = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const updatePendingFeature = useCadStore((s) => s.updatePendingFeature);
    const setPendingFeature = useCadStore((s) => s.setPendingFeature);
    const selection = useCadStore((s) => s.selection);
    const setSelection = useCadStore((s) => s.setSelection);
    const selectedFaceData = useCadStore((s) => s.selectedFaceData);
    const addFeature = useCadStore((s) => s.addFeature);
    const updateFeature = useCadStore((s) => s.updateFeature);

    // Когда юзер кликает по грани — записываем её в faceInfo.
    React.useEffect(() => {
        if (!pendingFeature || pendingFeature.kind !== 'hole') return;
        if (selection.kind !== 'face' || !selectedFaceData?.planeRef) return;
        updatePendingFeature({
            faceInfo: {
                bodyId: selectedFaceData.bodyId,
                faceId: selectedFaceData.faceId,
                planeRef: selectedFaceData.planeRef,
                faceCenter: selectedFaceData.planeRef.center || null,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, selectedFaceData?.bodyId, selectedFaceData?.faceId]);

    if (!pendingFeature || pendingFeature.kind !== 'hole') return null;
    const {
        faceInfo, diameter, depth, depthType,
        offsetX = 0, offsetY = 0, editingFeatureId,
    } = pendingFeature;
    const hasFace = !!(faceInfo && faceInfo.planeRef && faceInfo.faceCenter);
    const isBlind = depthType === 'blind';

    const onCancel = () => {
        setPendingFeature(null);
        setSelection(null, []);
    };

    const onOk = () => runExclusive(async () => {
        if (!hasFace) {
            toast('Выбери грань тела (кликни по поверхности)', 'error');
            return;
        }
        const dia = Number(diameter);
        if (!Number.isFinite(dia) || dia <= 0) {
            toast('Диаметр должен быть больше 0', 'error');
            return;
        }
        if (isBlind) {
            const dp = Number(depth);
            if (!Number.isFinite(dp) || dp <= 0) {
                toast('Глубина должна быть больше 0', 'error');
                return;
            }
        }
        const bodyId = faceInfo.bodyId;
        const holePayload = {
            targetBodyId: bodyId,
            planeRef: faceInfo.planeRef,
            faceCenter: faceInfo.faceCenter,
            offsetX: Number(offsetX) || 0,
            offsetY: Number(offsetY) || 0,
            diameter: dia,
            depth: Number(depth) || 0,
            depthType: depthType || 'blind',
        };

        // Поля фичи (для Timeline и rebuild).
        const featureData = {
            kind: 'hole',
            bodyId,
            faceId: faceInfo.faceId,
            planeRef: faceInfo.planeRef,
            faceCenter: faceInfo.faceCenter,
            offsetX: holePayload.offsetX,
            offsetY: holePayload.offsetY,
            diameter: dia,
            depth: holePayload.depth,
            depthType: holePayload.depthType,
            targetBodyIdx: getBodyIndex(bodyId),
        };

        if (editingFeatureId) {
            // Редактирование существующего отверстия — обновляем фичу и
            // полностью пересобираем модель из истории.
            updateFeature(editingFeatureId, featureData);
            try {
                await rebuildAllBodies();
            } catch (e) {
                console.error('[HoleForm] rebuild failed:', e);
                toast('Ошибка пересчёта: ' + (e?.message || e), 'error');
                return;
            }
            setPendingFeature(null);
            setSelection(null, []);
            return;
        }

        try {
            await getGeometryClient().createHole(holePayload);
            addFeature({
                id: `feat_${Date.now().toString(36)}`,
                ...featureData,
                createdAt: Date.now(),
            });
        } catch (e) {
            console.error('[HoleForm] failed:', e);
            toast('Ошибка отверстия: ' + (e?.message || e), 'error');
            return;
        }
        setPendingFeature(null);
        setSelection(null, []);
    });

    return (
        <div className={cl.featureForm}>
            <div className={cl.formTitle}>{RU.create.hole}</div>

            <FormRow label="Грань">
                {hasFace ? (
                    <span className={cl.profileBadge}>✓ Грань выбрана</span>
                ) : (
                    <span className={cl.profileBadgeNeed}>
                        Кликни по грани тела
                    </span>
                )}
            </FormRow>

            <FormRow label="Диаметр">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        min="0.1"
                        className={cl.numInput}
                        value={diameter}
                        onChange={(e) => updatePendingFeature({
                            diameter: parseFloat(e.target.value) || 0,
                        })}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <FormRow label="Тип">
                <select
                    className={cl.select}
                    value={depthType}
                    onChange={(e) => updatePendingFeature({
                        depthType: e.target.value,
                    })}
                >
                    <option value="through">Насквозь</option>
                    <option value="blind">На глубину</option>
                </select>
            </FormRow>

            {isBlind && (
                <FormRow label="Глубина">
                    <div className={cl.inputWithUnit}>
                        <input
                            type="number"
                            step="any"
                            min="0.1"
                            className={cl.numInput}
                            value={depth}
                            onChange={(e) => updatePendingFeature({
                                depth: parseFloat(e.target.value) || 0,
                            })}
                        />
                        <span className={cl.unit}>мм</span>
                    </div>
                </FormRow>
            )}

            <FormRow label="Смещение X">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        className={cl.numInput}
                        value={offsetX}
                        onChange={(e) => updatePendingFeature({
                            offsetX: parseFloat(e.target.value) || 0,
                        })}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <FormRow label="Смещение Y">
                <div className={cl.inputWithUnit}>
                    <input
                        type="number"
                        step="any"
                        className={cl.numInput}
                        value={offsetY}
                        onChange={(e) => updatePendingFeature({
                            offsetY: parseFloat(e.target.value) || 0,
                        })}
                    />
                    <span className={cl.unit}>мм</span>
                </div>
            </FormRow>

            <div style={{ fontSize: 11, color: '#888', padding: '4px 0' }}>
                Отверстие сверлится в центре грани. Смещение X/Y сдвигает
                его по плоскости грани.
            </div>

            <div className={cl.formActions}>
                <button
                    className={`${cl.btn} ${cl.btnPrimary}`}
                    onClick={onOk}
                    disabled={!hasFace}
                >
                    OK
                </button>
                <button className={`${cl.btn} ${cl.btnSecondary}`} onClick={onCancel}>
                    {RU.operationParams.cancel}
                </button>
            </div>
        </div>
    );
};


/*
 * Свойства выделенной геометрии эскиза.
 *
 * 🔴 ЗАЧЕМ. Линия, которой отмеряют отступ («20 мм от края»), по смыслу
 * ВСПОМОГАТЕЛЬНАЯ: она нужна, чтобы к ней привязаться, а не чтобы делить
 * фигуру. Но обычная линия участвует в разбиении на области, поэтому
 * отрезала от прямоугольника полосу — и «Выдавить» брал в работу её вместо
 * круга, а круг, через который она проходила, разрезался пополам.
 *
 * Инструмент «Вспомогательная линия» в редакторе есть, но спрятан в подменю,
 * и переключить УЖЕ НАРИСОВАННУЮ линию было нечем: приходилось удалять и
 * рисовать заново нужным инструментом. Теперь это одна галочка.
 */
const SketchPrimitiveForm = ({ ids }) => {
    const sketches = useCadStore((s) => s.doc.sketches);
    const activeSketchId = useCadStore((s) => s.activeSketchId);
    const updateSketchPrimitive = useCadStore((s) => s.updateSketchPrimitive);

    const sketch = sketches.find((s) => s.id === activeSketchId);
    const prims = (sketch?.primitives || []).filter((p) => ids.includes(p.id));
    // Переключать имеет смысл только линии и дуги: точки контур не образуют,
    // а окружность-«вспомогательная» — редкий случай, но тоже законный.
    const switchable = prims.filter(
        (p) => p.kind === 'line' || p.kind === 'arc' || p.kind === 'circle');
    const allAux = switchable.length > 0 && switchable.every((p) => !!p.construction);

    return (
        <div>
            <div className={cl.sectionTitle}>Геометрия эскиза</div>
            <div>Выбрано: <b>{prims.length || ids.length}</b></div>
            {switchable.length > 0 && (
                <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                        marginTop: 10, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={allAux}
                            onChange={(e) => {
                                const v = e.target.checked;
                                for (const p of switchable) {
                                    updateSketchPrimitive(p.id, { construction: v });
                                }
                            }}
                        />
                        <span>Вспомогательная геометрия</span>
                    </label>
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7, lineHeight: 1.4 }}>
                        Такая линия служит только разметкой: к ней можно
                        привязываться, но контур она не образует и не делит
                        фигуры при выдавливании.
                    </div>
                </>
            )}
        </div>
    );
};


const Properties = () => {
    const pendingFeature = useCadStore((s) => s.pendingFeature);
    const selection = useCadStore((s) => s.selection);
    const bodyRef = React.useRef(null);

    /*
     * ENTER = ОК, ESCAPE = ОТМЕНА — для ЛЮБОЙ формы операции.
     *
     * Раньше Enter в поле размера только снимал фокус (ExprInput), а до
     * кнопки не доходил: приходилось тянуться мышью к «ОК», хотя число
     * уже введено. Особенно заметно в выдавливании, где ввод расстояния —
     * последнее действие перед подтверждением.
     *
     * Решение общее, а не для одной формы: форм с кнопкой ОК двадцать одна,
     * и дописывать обработчик в каждую значило бы разъезжающееся поведение.
     * Панель находит СВОЮ главную кнопку в разметке и нажимает её —
     * поэтому вся логика (валидность, disabled, async-guard) остаётся
     * внутри формы и не дублируется.
     *
     * disabled-кнопка не нажмётся: click по ней браузер игнорирует, то есть
     * незаполненная форма по Enter не «применится» наполовину.
     */
    React.useEffect(() => {
        if (!pendingFeature) return undefined;

        const onKeyDown = (e) => {
            if (e.ctrlKey || e.altKey || e.metaKey) return;
            if (e.key !== 'Enter' && e.key !== 'Escape') return;

            const el = document.activeElement;
            // В textarea Enter — это перевод строки, туда не вмешиваемся.
            if (el?.tagName === 'TEXTAREA' || el?.isContentEditable) return;
            // В выпадающем списке Enter выбирает пункт — тоже не наше дело.
            if (el?.tagName === 'SELECT') return;

            const root = bodyRef.current;
            if (!root) return;
            const buttons = root.querySelectorAll('button');
            // Главная кнопка — первая в блоке действий; отмена идёт следом.
            let ok = null, cancel = null;
            for (const b of buttons) {
                if (b.parentElement?.className?.includes?.('formActions')) {
                    if (!ok) ok = b;
                    else if (!cancel) cancel = b;
                }
            }
            const target = e.key === 'Enter' ? ok : cancel;
            if (!target || target.disabled) return;

            e.preventDefault();
            // Снимаем фокус с поля: ExprInput применяет значение на blur,
            // иначе набранное число могло не успеть уйти в модель.
            if (el?.tagName === 'INPUT') el.blur();
            target.click();
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [pendingFeature]);

    return (
        <div className={cl.props}>
            <div className={cl.header}>{RU.common.properties}</div>
            <div className={cl.body} ref={bodyRef}>
                {pendingFeature?.kind === 'extrude' && <ExtrudeForm/>}
                {pendingFeature?.kind === 'text' && <TextForm/>}
                {pendingFeature?.kind === 'coil' && <CoilForm/>}
                {pendingFeature?.kind === 'scale' && <ScaleForm/>}
                {pendingFeature?.kind === 'revolve' && <RevolveForm/>}
                {pendingFeature?.kind === 'sweep' && <SweepForm/>}
                {pendingFeature?.kind === 'loft' && <LoftForm/>}
                {pendingFeature?.kind === 'primitive' && <PrimitiveForm/>}
                {pendingFeature?.kind === 'combine' && <CombineForm/>}
                {pendingFeature?.kind === 'joint' && <JointForm/>}
                {pendingFeature?.kind === 'rib' && <RibForm/>}
                {pendingFeature?.kind === 'moveCopy' && <MoveCopyForm/>}
                {pendingFeature?.kind === 'shell' && <ShellForm/>}
                {pendingFeature?.kind === 'hole' && <HoleForm/>}
                {pendingFeature?.kind === 'mirror' && <MirrorForm/>}
                {pendingFeature?.kind === 'rectPattern' && <RectPatternForm/>}
                {pendingFeature?.kind === 'circPattern' && <CircPatternForm/>}
                {pendingFeature?.kind === 'offsetPlane' && <OffsetPlaneForm/>}
                {pendingFeature?.kind === 'planeAtAngle' && <AngledPlaneForm/>}
                {pendingFeature?.kind === 'midplane' && <MidplaneForm/>}
                {pendingFeature?.kind === 'planeThroughThree' && <PlaneThrough3Form/>}
                {pendingFeature?.kind === 'workaxis' && <WorkaxisForm/>}
                {pendingFeature?.kind === 'measure' && <MeasureForm/>}
                {(pendingFeature?.kind === 'fillet' || pendingFeature?.kind === 'chamfer')
                    && <FilletChamferForm/>}

                {!pendingFeature && selection.kind === null && (
                    <div className={cl.empty}>
                        Выбери объект или запусти операцию из меню «Создать»
                    </div>
                )}

                {!pendingFeature && selection.kind === 'sketch_primitive' && (
                    <SketchPrimitiveForm ids={selection.ids}/>
                )}

                {!pendingFeature && selection.kind && selection.kind !== 'sketch_primitive' && (
                    <div>
                        <div className={cl.sectionTitle}>Выделение</div>
                        <div>Тип: <b>{selection.kind}</b></div>
                        <div>Количество: <b>{selection.ids.length}</b></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Properties;
